import React, { useState, useEffect, useRef } from 'react';
import { getDocIcon } from '../../utils/docTypeIcons';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Card,
  CardContent,
  TextField,
  Divider,
  useMediaQuery,
  useTheme,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Backdrop,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  alpha
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  PlayArrow as StartIcon,
  Pause as HoldIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  Receipt as InvoiceIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  AutoAwesome as ExtractIcon,
  Download as DownloadIcon,
  Description as FileIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Search as SearchIcon,
  PersonAdd as AssignIcon,
  ConfirmationNumber as TicketIcon,
  Assignment as InternalTaskIcon,
  AddTask as CreateTaskIcon,
  OpenInNew as OpenTaskIcon,
  HowToReg as AssignedIcon,
  Replay as RevertIcon,
  Business as B2bIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import WorkOrderDocumentRows from '../../components/work-orders/WorkOrderDocumentRows';
import {
  loadEmployeeDocGroups, loadOrgDocGroups, currentGroupOf, fetchPrefilledDoc, prefillKindOf,
  submitWorkOrderDocuments, needsEmployeeSync
} from '../../components/work-orders/documentPrefill';
import AddWorkOrderCustomerPanel from './AddWorkOrderCustomerPanel';
import { splitBySize, oversizeMessage } from '../../utils/fileSizeLimit';
import { resolveExtractedDocNumber } from '../../utils/extractionUtils';
import { isPrimaryDocType, isOrganizationDocType } from '../../utils/docTypeGroups';
import TaskService from '../../services/task.service';
import internalServiceService from '../../services/internal-service.service';
import WalletService from '../../services/wallet.service';
import WorkOrderPaymentDialog, {
  lineItemsToPay, needsOutcome, PaidItemOutcomes, outcomesComplete, outcomesBody,
} from './WorkOrderPaymentDialog';
import { workOrderMoneyService } from '../../services/accounting/workOrderMoney.service';
import { serverMessage } from '../../utils/apiConvert';
import { transactionNumbersOf, transactionNumberChoices } from '../../utils/transactionNumbers';
import TransactionNumberChoices from '../../components/work-orders/TransactionNumberChoices';

// Pull the backend's error message (ApiResponse .error / .message) out of an axios error.
const apiErr = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.message || err?.message || fallback;

const AddServiceDialog = ({ open, onClose, onSubmit, submitting, workOrder, customers = [], showThaklisServices = false, initialItem = null, preferredCustomerName = null }) => {
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [markupAmount, setMarkupAmount] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // KAN-29: 0 = internal services, 1 = Thaklis services (second tab shown only when enabled).
  const [serviceTab, setServiceTab] = useState(0);
  // Custom field values (keyed by fieldId) + staged document uploads for the selected service.
  const [fieldValues, setFieldValues] = useState({});
  const [stagedDocs, setStagedDocs] = useState([]); // {tempId, file, code, label, fileName}
  const [dialogError, setDialogError] = useState('');
  // Edit mode: the same form pre-filled with an existing line item; the chosen service replaces it.
  const isEdit = Boolean(initialItem);

  const fieldKey = (f) => f.fieldId || f.tcCustomFieldId || f.id || f.code;

  // Seed field values from the org defaults on the chosen service (like the create page), then, in
  // edit mode for the same service, overlay the line item's already-saved values. Reset staged docs.
  useEffect(() => {
    if (!selectedService) { setFieldValues({}); setStagedDocs([]); setDialogError(''); return; }
    const seeded = {};
    (selectedService.customFields || selectedService.custom_fields || []).forEach(f => {
      seeded[fieldKey(f)] = f.orgDefault ?? f.value ?? '';
    });
    if (initialItem && String(selectedService.id) === String(initialItem.service_id || initialItem.serviceId)) {
      (initialItem.field_values || initialItem.fieldValues || []).forEach(fv => {
        const fid = fv.fieldId || fv.field_id;
        if (fid != null) seeded[fid] = fv.value ?? '';
      });
    }
    setFieldValues(seeded);
    setStagedDocs([]);
    setDialogError('');
  }, [selectedService, initialItem]);

  // A required/expected doc is already satisfied when the WO already holds a document of that type
  // for the selected applicant (matched by code or label, case-insensitive).
  const docCoveredInWo = (reqCode, reqLabel) => {
    const custName = (selectedCustomer?.name || '').trim().toLowerCase();
    return (workOrder?.documents || []).some(d => {
      const dt = String(d.document_type || d.documentType || '').trim().toLowerCase();
      const dcust = String(d.customer_name || d.customerName || '').trim().toLowerCase();
      const typeMatch = dt === String(reqCode || '').toLowerCase() || dt === String(reqLabel || '').toLowerCase();
      return typeMatch && (!custName || dcust === custName);
    });
  };

  const handleFieldChange = (fid, value) => setFieldValues(prev => ({ ...prev, [fid]: value }));

  const stageDoc = (reqDoc, fileList) => {
    const { ok: files, tooBig } = splitBySize(Array.from(fileList || []));
    if (tooBig.length) setDialogError(tooBig.map(oversizeMessage).join(' '));
    if (!files.length) return;
    setStagedDocs(prev => [
      ...prev,
      ...files.map((file, i) => ({ tempId: `${Date.now()}-${i}-${file.name}`, file, code: reqDoc.code, label: reqDoc.label, fileName: file.name })),
    ]);
  };
  const removeStaged = (tempId) => setStagedDocs(prev => prev.filter(s => s.tempId !== tempId));

  useEffect(() => {
    if (!open) return;
    setSelectedService(null);
    setServiceTab(0);
    // Prefill the markup (stored exclusive + its VAT) back to the inclusive amount the form expects.
    const initMarkupIncl = initialItem
      ? (parseFloat(initialItem.markup_amount ?? initialItem.markupAmount ?? 0) || 0)
        + (parseFloat(initialItem.tax_on_markup ?? initialItem.taxOnMarkup ?? 0) || 0)
      : 0;
    setMarkupAmount(initMarkupIncl > 0 ? initMarkupIncl.toFixed(2) : '');
    // Default the applicant to the only one when there's a single customer, else let the user pick.
    // Single customer → that one; opened from a customer tab → that tab's customer; else let the user pick.
    setSelectedCustomer(customers.length === 1
      ? customers[0]
      : (customers.find(c => preferredCustomerName && c.name === preferredCustomerName) || null));
    setLoadingServices(true);
    // Resolve internal-service prices for this WO's organization (walk-in / no org → uniform only).
    const orgId = workOrder?.organization_id || workOrder?.organizationId || null;
    internalServiceService.getServicesForLineItems(orgId)
      .then((allServices) => {
        setServices(allServices);
        // In edit mode, pre-select the line item's current service + applicant.
        if (initialItem) {
          const initServiceId = initialItem.service_id || initialItem.serviceId;
          const match = allServices.find((s) => String(s.id) === String(initServiceId));
          if (match) {
            setSelectedService(match);
            setServiceTab(match.type === 'INTERNAL' ? 0 : 1);
          }
          const initCustName = initialItem.customer_name || initialItem.customerName;
          if (initCustName) {
            const c = customers.find((x) => x.name === initCustName);
            if (c) setSelectedCustomer(c);
          }
        }
      })
      .catch((err) => console.error('Error fetching services:', err))
      .finally(() => setLoadingServices(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, workOrder, initialItem]);

  const handleSubmit = () => {
    if (!selectedService) return;
    const customFields = selectedService.customFields || selectedService.custom_fields || [];
    const docReqs = selectedService.documentRequirements || selectedService.document_requirements || [];

    // Hard-block: every required custom field must have a value.
    for (const f of customFields) {
      if (f.required && !String(fieldValues[fieldKey(f)] ?? '').trim()) {
        setDialogError(`Please fill the required field "${f.label}".`);
        return;
      }
    }
    // Hard-block: every required document must already be on the WO or staged here.
    const stagedTypes = new Set(stagedDocs.map(s => s.code));
    for (const r of docReqs) {
      if (r.required && !docCoveredInWo(r.code, r.label) && !stagedTypes.has(r.code)) {
        setDialogError(`Please attach the required document "${r.label}".`);
        return;
      }
    }
    setDialogError('');

    const VAT_RATE = 0.05;
    // Services already return exclusive service_charge and separate taxOnServiceCharge
    const serviceCharge = selectedService.service_charge || selectedService.serviceCharge || 0;
    const serviceTax = selectedService.tax_on_service_charge || selectedService.taxOnServiceCharge || 0;
    const govtCharge = selectedService.govt_charge || selectedService.govtCharge || 0;
    // Markup entered by user is inclusive of VAT — split using same rounding as invoice
    const markupInclVat = parseFloat(markupAmount) || 0;
    const markupBase = Number((markupInclVat / 1.05).toFixed(2));
    const markupVat = Number((markupInclVat - markupBase).toFixed(2));
    // line_total = all-inclusive (sc + gc + markup + tax + taxOnMarkup)
    const lineTotal = serviceCharge + govtCharge + markupBase + serviceTax + markupVat;
    const originalLineTotal = serviceCharge + govtCharge + serviceTax;

    const itemData = {
      service_id: selectedService.id,
      service_name: selectedService.name,
      service_description: selectedService.description || '',
      quantity: 1,
      unit_price: serviceCharge + govtCharge,
      service_charge: serviceCharge,
      govt_charge: govtCharge,
      tax_value: serviceTax,
      tax_on_markup: markupVat,
      line_total: lineTotal,
      original_line_total: originalLineTotal,
      markup_amount: markupBase,
      is_custom_service: selectedService.type === 'INTERNAL',
      creates_task: selectedService.createsTask || selectedService.creates_task || false,
      // Per-applicant identity for this line (same source as the document upload picker).
      customer_name: selectedCustomer?.name || null,
      customer_id_number: selectedCustomer?.passportNumber || null,
      customer_emirates_id: selectedCustomer?.emiratesId || null,
      // Custom field values captured in the dialog (org defaults + any edits), non-empty only.
      field_values: (selectedService.customFields || selectedService.custom_fields || [])
        .map(f => ({ fieldId: fieldKey(f), code: f.code, label: f.label, value: fieldValues[fieldKey(f)] }))
        .filter(fv => fv.value != null && String(fv.value).trim() !== '')
    };

    // Staged docs are uploaded to the WO by the parent after the line item is saved.
    onSubmit(itemData, stagedDocs);
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (!submitting) onClose(); }}
      disableEscapeKeyDown={submitting}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{isEdit ? 'Update Service' : 'Add Service'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {dialogError && <Alert severity="error" onClose={() => setDialogError('')}>{dialogError}</Alert>}
          {/* KAN-29: internal services on the first tab; Thaklis services on the second,
              shown only when Thaklis services are enabled in Work Order Settings. */}
          {showThaklisServices && (
            <Box>
              <Tabs
                value={serviceTab}
                onChange={(e, v) => { setServiceTab(v); setSelectedService(null); }}
                sx={{ minHeight: 36 }}
              >
                <Tab label="Internal Services" sx={{ minHeight: 36, py: 0 }} />
                <Tab label="Thaklis Services" sx={{ minHeight: 36, py: 0 }} />
              </Tabs>
              {serviceTab === 1 && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  These services are provided by Thaklis — adding them means you are planning to give this work to Thaklis.
                </Alert>
              )}
            </Box>
          )}
          <Autocomplete
            options={
              showThaklisServices
                ? services.filter(s => (serviceTab === 0 ? s.type === 'INTERNAL' : s.type !== 'INTERNAL'))
                : services.filter(s => s.type === 'INTERNAL')
            }
            loading={loadingServices}
            getOptionLabel={(option) => `${option.name || ''}${option.type === 'INTERNAL' ? ' [Internal]' : ''}`}
            value={selectedService}
            onChange={(_, value) => setSelectedService(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Service"
                size="small"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingServices ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          {selectedService && (
            <Box sx={{ p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Service Charge: AED {(selectedService.service_charge || selectedService.serviceCharge || 0).toFixed(2)} &nbsp;|&nbsp;
                Govt Charge: AED {(selectedService.govt_charge || selectedService.govtCharge || 0).toFixed(2)}
              </Typography>
            </Box>
          )}
          {customers.length > 0 && (
            <Autocomplete
              options={customers}
              getOptionLabel={(o) => (o.passportNumber ? `${o.name} (${o.passportNumber})` : o.name)}
              isOptionEqualToValue={(o, v) => o.name === v.name}
              value={selectedCustomer}
              onChange={(_, value) => setSelectedCustomer(value)}
              renderInput={(params) => (
                <TextField {...params} label="Applicant / Customer" size="small"
                  helperText="Which applicant this service is for" />
              )}
            />
          )}
          <TextField
            fullWidth
            size="small"
            label="Markup Amount (AED, incl. VAT)"
            type="number"
            value={markupAmount}
            onChange={(e) => setMarkupAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />

          {/* Service Information — custom fields for the chosen service (seeded from org defaults) */}
          {selectedService && (selectedService.customFields || selectedService.custom_fields || []).length > 0 && (
            <>
              <Divider textAlign="left" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Service Information</Typography>
              </Divider>
              {(selectedService.customFields || selectedService.custom_fields || []).map((f) => {
                const fid = fieldKey(f);
                const label = (f.label || f.code) + (f.required ? ' *' : '');
                const val = fieldValues[fid] ?? '';
                if (f.fieldType === 'SELECT') {
                  return (
                    <TextField key={fid} select fullWidth size="small" label={label} value={val}
                      onChange={(e) => handleFieldChange(fid, e.target.value)}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {(f.options || []).map((opt, oi) => (
                        <MenuItem key={oi} value={typeof opt === 'string' ? opt : (opt.value || '')}>
                          {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }
                if (f.fieldType === 'DATE') {
                  return (
                    <TextField key={fid} fullWidth size="small" type="date" label={label} value={val}
                      onChange={(e) => handleFieldChange(fid, e.target.value)} InputLabelProps={{ shrink: true }} />
                  );
                }
                return (
                  <TextField key={fid} fullWidth size="small" label={label} value={val}
                    type={f.fieldType === 'NUMBER' ? 'number' : 'text'}
                    multiline={f.fieldType === 'TEXTAREA'} rows={f.fieldType === 'TEXTAREA' ? 2 : undefined}
                    onChange={(e) => handleFieldChange(fid, e.target.value)} />
                );
              })}
            </>
          )}

          {/* Documents — required/expected docs for the chosen service. Docs already on the work
              order for this applicant aren't asked for again. */}
          {selectedService && (selectedService.documentRequirements || selectedService.document_requirements || []).length > 0 && (
            <>
              <Divider textAlign="left" sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Documents</Typography>
              </Divider>
              {(selectedService.documentRequirements || selectedService.document_requirements || []).map((r) => {
                const covered = docCoveredInWo(r.code, r.label);
                const staged = stagedDocs.filter(s => s.code === r.code);
                return (
                  <Box key={r.code} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {r.label}{r.required ? ' *' : ''}
                      </Typography>
                      {covered ? (
                        <Chip size="small" color="success" label="Already in work order" sx={{ fontSize: '0.65rem', height: 20 }} />
                      ) : (
                        <Chip size="small" variant="outlined" color={r.required ? 'error' : 'default'}
                          label={r.required ? 'Required' : 'Optional'} sx={{ fontSize: '0.65rem', height: 20 }} />
                      )}
                    </Box>
                    {!covered && (
                      <Box sx={{ mt: 1 }}>
                        {staged.map(s => (
                          <Box key={s.tempId} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>{s.fileName}</Typography>
                            <IconButton size="small" onClick={() => removeStaged(s.tempId)}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        ))}
                        <Button size="small" variant="outlined" component="label" startIcon={<UploadIcon />}
                          sx={{ textTransform: 'none' }}>
                          {staged.length > 0 ? 'Add another file' : 'Upload'}
                          <input type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                            onChange={(e) => { stageDoc(r, e.target.files); e.target.value = ''; }} />
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!selectedService || submitting}
          startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{
            '&:disabled': { bgcolor: 'primary.light', color: '#fff' }
          }}
        >
          {submitting ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save' : 'Add Service')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WorkOrderDetail = ({ currentUser }) => {
  const navigate = useNavigate();
  const { workOrderId } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  // Tasks / services / documents shown per customer (tabs, default) or all together; remembered per browser.
  const [detailView, setDetailView] = useState(() => {
    try { return localStorage.getItem('woDetailView') || 'customer'; } catch (e) { return 'customer'; }
  });
  // Selected customer tab key (lowercased name, '__general' or '__add'); null = first tab.
  const [customerTab, setCustomerTab] = useState(null);
  const [workOrder, setWorkOrder] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Line money: payments and add-ons (BE-3)
  const [paymentItemId, setPaymentItemId] = useState(null);
  const [linePayments, setLinePayments] = useState([]);
  const [lineAddons, setLineAddons] = useState([]);
  const [tcBankAccounts, setTcBankAccounts] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [removeLineTarget, setRemoveLineTarget] = useState(null);

  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');

  // Document upload
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadCustomerName, setUploadCustomerName] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  // Upload dialog, as on WO create: types picked from "Add document" are rows, pre-filled from what is on file
  const [uploadTypes, setUploadTypes] = useState([]);        // codes of the rows
  const [uploadDocs, setUploadDocs] = useState([]);          // staged files (pre-filled or added)
  const [uploadPrefillTried, setUploadPrefillTried] = useState([]);
  const [uploadEmpGroups, setUploadEmpGroups] = useState(null);
  const [uploadOrgGroups, setUploadOrgGroups] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extractingDocs, setExtractingDocs] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocTypes, setLoadingDocTypes] = useState(false);

  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Document preview
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Ticket linking
  const [linkTicketDialogOpen, setLinkTicketDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [organizationTickets, setOrganizationTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketSearchQuery, setTicketSearchQuery] = useState('');
  // Live Thaklis ticket status by ticket id, for linked WO tasks
  const [ticketStatuses, setTicketStatuses] = useState({});

  // B2B hand-off (a connections-enabled TC → a B2B provider). Live B2B work-order status keyed by task id.
  const isPromoted = currentUser?.isPromoted === true || currentUser?.connectionsEnabled === true;
  const [b2bWoStatuses, setB2bWoStatuses] = useState({});
  const [b2bDialogOpen, setB2bDialogOpen] = useState(false);
  const [b2bServices, setB2bServices] = useState([]);
  const [b2bServicesLoading, setB2bServicesLoading] = useState(false);
  const [b2bSubmitting, setB2bSubmitting] = useState(false);
  const [b2bPartners, setB2bPartners] = useState([]);
  const [selectedB2bConnectionId, setSelectedB2bConnectionId] = useState('');
  const [selectedB2bServiceId, setSelectedB2bServiceId] = useState('');
  // Documents of the task's applicant, matched by customer name (the only applicant
  // link a document carries) and shared onto the delegated B2B work order.
  const [b2bDocs, setB2bDocs] = useState([]);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  // Existing WO doc reused to satisfy a required doc slot: { [requiredCode]: docId }. Auto-filled
  // from a matching WO doc, or set manually; an assigned doc is copied re-tagged with the code and
  // dropped from the "other documents" list.
  const [reqDocPick, setReqDocPick] = useState({});
  // Custom field values for the selected provider service (keyed by its catalog field id).
  const [b2bFieldValues, setB2bFieldValues] = useState({});
  // Files staged for manual upload during delegation: [{tempId, file, documentType, fileName}].
  const [b2bStagedDocs, setB2bStagedDocs] = useState([]);
  // The client line's service name, used to auto-match a provider service by name (no catalog link).
  const [b2bMatchName, setB2bMatchName] = useState('');
  // Single-provider fast path: when the name match is unique we skip the full dialog and confirm.
  const [b2bConfirmOpen, setB2bConfirmOpen] = useState(false);
  const [b2bAutoMatch, setB2bAutoMatch] = useState(null);

  // Deliverable documents for a completed service (WO task): attach the finished output to the task.
  const [taskDocTask, setTaskDocTask] = useState(null);
  const [taskDocFiles, setTaskDocFiles] = useState([]);
  const [taskDocTxnNo, setTaskDocTxnNo] = useState('');
  const [taskDocUnticked, setTaskDocUnticked] = useState([]);
  // Deliverable doc type: 'APPLICATION_FORM' (default) or 'OTHER'; taskDocTypeOther holds the
  // optional custom name used when 'OTHER' is selected.
  const [taskDocType, setTaskDocType] = useState('APPLICATION_FORM');
  const [taskDocTypeOther, setTaskDocTypeOther] = useState('');
  const [taskDocExtracting, setTaskDocExtracting] = useState(false);
  const [taskDocError, setTaskDocError] = useState(null);
  const [taskDocUploading, setTaskDocUploading] = useState(false);

  // Email a completed service's deliverable documents to the client.
  const [emailTask, setEmailTask] = useState(null);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailLoadingRecipients, setEmailLoadingRecipients] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Employee assignment (for internal tasks)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');

  // Internal task creation (stores taskNumber by work order task ID)
  const [createdTasks, setCreatedTasks] = useState({});
  const [creatingTask, setCreatingTask] = useState(null);

  // Work Order Assignment (entire WO to employee)
  const [woAssignDialogOpen, setWoAssignDialogOpen] = useState(false);
  const [assigningWorkOrder, setAssigningWorkOrder] = useState(false);

  // Create Invoice from WO: mapped organization picker
  const [createInvoiceDialogOpen, setCreateInvoiceDialogOpen] = useState(false);
  const [mappedOrgOptions, setMappedOrgOptions] = useState([]);
  const [selectedMappedOrg, setSelectedMappedOrg] = useState(null);
  const [loadingMappedOrgs, setLoadingMappedOrgs] = useState(false);

  // Add Service dialog
  const [addServiceDialogOpen, setAddServiceDialogOpen] = useState(false);
  const [addingService, setAddingService] = useState(false);
  // Update Service dialog — the same form pre-filled with an existing line item.
  const [editingLineItem, setEditingLineItem] = useState(null);
  const [updatingService, setUpdatingService] = useState(false);

  // KAN-29: whether Thaklis services may be added on work orders for this TC (Work Order Settings —
  // default off). Gated additionally by the TC's Thaklis-features toggle. When off, the Add Service
  // dialog shows internal services only.
  const thaklisDisabled = currentUser?.thaklisEnabled === false;
  const [thaklisServicesEnabled, setThaklisServicesEnabled] = useState(false);
  const showThaklisServices = !thaklisDisabled && thaklisServicesEnabled;

  // Line-item markup apply/edit/remove
  const [markupDialogOpen, setMarkupDialogOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [lineItemMarkupAmount, setLineItemMarkupAmount] = useState('');
  const [applyingLineItemMarkup, setApplyingLineItemMarkup] = useState(false);
  const LINE_ITEM_VAT_RATE = 0.05;

  useEffect(() => {
    if (workOrderId) {
      fetchWorkOrder();
      fetchDocumentTypes();
      loadMoney();
      WalletService.getBankAccounts().then(setTcBankAccounts).catch(() => {});
    }
  }, [workOrderId]);

  const loadMoney = async () => {
    try {
      const [p, a] = await Promise.all([
        workOrderMoneyService.getPayments(workOrderId),
        workOrderMoneyService.getAddons(workOrderId),
      ]);
      setLinePayments(p);
      setLineAddons(a);
    } catch (err) {
      setLinePayments([]);
      setLineAddons([]);
    }
  };

  const paidOutcomePayments = (lineItemId) =>
    linePayments.filter((p) => needsOutcome(p) && (!lineItemId || p.lineItemId === lineItemId));

  // Load the WO Thaklis-services flag once the work order (hence its TC) is known.
  useEffect(() => {
    const tcId = workOrder?.typingCenterId || workOrder?.typing_center_id;
    if (!tcId) return;
    ApiService.getWorkOrderConfig(tcId)
      .then((resp) => {
        const c = resp?.data?.data || resp?.data || {};
        setThaklisServicesEnabled(c.thaklis_services_enabled === true);
      })
      .catch(() => setThaklisServicesEnabled(false));
  }, [workOrder]);

  const fetchDocumentTypes = async () => {
    setLoadingDocTypes(true);
    try {
      // Prefer this typing center's own document types; fall back to the Thaklis
      // standard list only when the TC hasn't defined any yet.
      const tcDocTypes = await import('../../services/tc-document-type.service')
        .then(m => m.default.getActive()).catch(() => []);
      let types;
      if (tcDocTypes.length > 0) {
        const categoryOrder = ['Identity', 'Immigration', 'Medical', 'Employment', 'Insurance', 'Company', 'Financial', 'Legal', 'Other'];
        types = [...tcDocTypes].sort((a, b) => {
          const ai = categoryOrder.indexOf(a.category || 'Other');
          const bi = categoryOrder.indexOf(b.category || 'Other');
          if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
          return (a.sortOrder || 0) - (b.sortOrder || 0);
        }).map(dt => ({ code: dt.code, label: dt.label, category: dt.category, holderType: dt.holder_type || dt.holderType, isPrimary: isPrimaryDocType(dt) }));
      } else {
        const response = await ApiService.getDocumentTypes();
        types = response?.data?.data || response?.data || [];
      }
      setDocumentTypes(types);
    } catch (err) {
      console.error('Error fetching document types:', err);
      setDocumentTypes([]);
    } finally {
      setLoadingDocTypes(false);
    }
  };

  const fetchWorkOrder = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await ApiService.getWorkOrderById(workOrderId);
      const data = response?.data?.data || response?.data;
      setWorkOrder(data);

      // Fetch the live Thaklis status of every linked ticket so the UI can show it next to
      // the ticket number and warn on completion when it isn't COMPLETED.
      const woTasks = data?.tasks || [];
      const linkedTicketIds = [...new Set(woTasks
        .map((t) => t.thaklisTicketId || t.thaklis_ticket_id)
        .filter(Boolean))];
      if (linkedTicketIds.length > 0) {
        Promise.all(linkedTicketIds.map(async (tid) => {
          try {
            const resp = await ApiService.getTicketById(tid);
            const ticket = resp?.data?.data || resp?.data;
            return [tid, ticket?.status || null];
          } catch (e) {
            return [tid, null];
          }
        })).then((pairs) => {
          const map = {};
          for (const [tid, st] of pairs) { if (st) map[tid] = st; }
          setTicketStatuses(map);
        });
      } else {
        setTicketStatuses({});
      }

      // Fetch the live status of every task delegated to the B2B, keyed by task id.
      const delegatedTasks = woTasks.filter((t) => t.b2bWorkOrderId || t.b2b_work_order_id);
      if (delegatedTasks.length > 0) {
        ApiService.getWorkOrderB2bStatuses(workOrderId).then((resp) => {
          const data = resp?.data?.data || {};
          const map = {};
          for (const [tid, entry] of Object.entries(data)) {
            if (entry?.status) map[tid] = entry.status;
          }
          setB2bWoStatuses(map);
        }).catch(() => setB2bWoStatuses({}));
      } else {
        setB2bWoStatuses({});
      }

      // Load existing internal tasks for this WO's tasks so the UI shows
      // the correct button (Assign vs Create Internal Task) on page load.
      const tcId = data?.typingCenterId || data?.typing_center_id;
      if (tcId) {
        try {
          const tasksResult = await TaskService.getAll({
            typingCenterId: tcId,
            workOrderId: workOrderId
          });
          const internalTasks = tasksResult?.data || [];
          const taskMap = {};
          for (const t of internalTasks) {
            const woTaskId = t.workOrderTaskId || t.work_order_task_id;
            if (woTaskId) {
              taskMap[woTaskId] = {
                id: t.id,
                taskNumber: t.taskNumber || t.task_number,
                assignedTo: t.assignedTo || t.assigned_to || null,
                assignedToName: t.assignedToName || t.assigned_to_name || null,
                status: t.status || null
              };
            }
          }
          setCreatedTasks(taskMap);
        } catch (err) {
          console.error('Error fetching internal tasks for WO:', err);
        }
      }
    } catch (err) {
      console.error('Error fetching work order:', err);
      setError('Failed to load work order');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusAction = async (action) => {
    setActionLoading(true);
    setError(null);
    try {
      switch (action) {
        case 'approve':
          await ApiService.approveWorkOrder(workOrderId);
          setSuccess('Work order approved and moved to OPEN');
          break;
        case 'start':
          await ApiService.startWorkOrder(workOrderId);
          setSuccess('Work order started successfully');
          break;
        case 'hold':
          await ApiService.holdWorkOrder(workOrderId);
          setSuccess('Work order put on hold');
          break;
        case 'complete':
          await ApiService.completeWorkOrder(workOrderId);
          setSuccess('Work order completed successfully');
          break;
        case 'cancel':
          {
            const paid = paidOutcomePayments();
            await workOrderMoneyService.cancelWorkOrder(workOrderId, cancelReason, paid.length ? outcomesBody(paid, outcomes) : undefined);
          }
          setOutcomes({});
          setSuccess('Work order cancelled');
          setCancelDialogOpen(false);
          setCancelReason('');
          break;
        default:
          break;
      }
      fetchWorkOrder();
      loadMoney();
    } catch (err) {
      console.error(`Error performing ${action}:`, err);
      const msg = serverMessage(err, `Failed to ${action} work order`);
      setError(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleTaskComplete = async (task) => {
    const taskId = task?.id || task;
    // Thaklis task: UI-only warning if the linked ticket isn't COMPLETED yet.
    const ticketId = task?.thaklisTicketId || task?.thaklis_ticket_id;
    if (ticketId) {
      const tStatus = ticketStatuses[ticketId];
      if (tStatus && tStatus !== 'COMPLETED') {
        const num = task?.thaklisTicketNumber || task?.thaklis_ticket_number || 'linked';
        if (!window.confirm(`Ticket ${num} is ${tStatus}, not COMPLETED. Complete this task anyway?`)) {
          return;
        }
      }
    }
    setActionLoading(true);
    try {
      await ApiService.completeWorkOrderTask(workOrderId, taskId);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error completing task:', err);
      setError(apiErr(err, 'Failed to complete task'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReopenTask = async (taskId) => {
    if (!window.confirm('Revert this task to Pending? You can then correct its ticket link or internal task.')) return;
    setActionLoading(true);
    try {
      await ApiService.reopenWorkOrderTask(workOrderId, taskId);
      setSuccess('Task reverted to pending');
      fetchWorkOrder();
    } catch (err) {
      console.error('Error reverting task:', err);
      setError(apiErr(err, 'Failed to revert task'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskName.trim()) {
      setError('Please enter a task name');
      return;
    }

    setActionLoading(true);
    try {
      await ApiService.addWorkOrderTask(workOrderId, {
        task_name: newTaskName,
        description: newTaskDescription
      });
      setTaskDialogOpen(false);
      setNewTaskName('');
      setNewTaskDescription('');
      fetchWorkOrder();
    } catch (err) {
      console.error('Error adding task:', err);
      setError(apiErr(err, 'Failed to add task'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    setActionLoading(true);
    try {
      await ApiService.deleteWorkOrderTask(workOrderId, taskId);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error deleting task:', err);
      setError(apiErr(err, 'Failed to delete task'));
    } finally {
      setActionLoading(false);
    }
  };

  // Distinct customers on this WO, derived from line items (name + passport/id number).
  // A WO customer is identified by name; the id number comes from the first line item
  // carrying that name (persisted as customer_id_number during WO creation).
  const getWoCustomers = () => {
    const items = workOrder?.lineItems || workOrder?.line_items || [];
    const map = new Map();
    items.forEach(li => {
      const name = li.customer_name || li.customerName || '';
      if (!name) return;
      const employeeUid = li.employee_uid || li.employeeUid || null;
      if (!map.has(name)) {
        map.set(name, {
          name,
          passportNumber: li.customer_id_number || li.customerIdNumber || '',
          emiratesId: li.customer_emirates_id || li.customerEmiratesId || '',
          // the Thaklis employee picked for this applicant on WO create (null on older work orders)
          employeeUid
        });
      } else if (employeeUid && !map.get(name).employeeUid) {
        map.get(name).employeeUid = employeeUid;
      }
    });
    return Array.from(map.values());
  };

  const woCustomers = getWoCustomers();
  const selectedUploadCustomer = woCustomers.find(c => c.name === uploadCustomerName) || null;
  // The work order's customer for employee / company documents: the client's internal org id, or walk-ins.
  const woCustomerType = workOrder?.customer_type || workOrder?.customerType || 'organization';
  const woOrgId = workOrder?.organization_id || workOrder?.organizationId || null;
  const woCustomerKey = woCustomerType === 'organization' ? woOrgId : 'walkin';
  const uploadCustomerChosen = woCustomers.length === 0 || !!uploadCustomerName;

  const resetUploadRows = () => {
    setUploadTypes([]);
    setUploadDocs([]);
    setUploadPrefillTried([]);
  };

  const openUploadDialog = () => {
    // Preselect the customer when the WO has exactly one.
    const custs = getWoCustomers();
    setUploadCustomerName(custs.length === 1 ? custs[0].name : '');
    resetUploadRows();
    setDocumentDescription('');
    setUploadDialogOpen(true);
  };

  // A different customer: their rows start over (a pre-fill still loading for the previous one is dropped).
  const uploadCustomerRef = useRef('');
  uploadCustomerRef.current = uploadCustomerName;
  const changeUploadCustomer = (name) => {
    setUploadCustomerName(name);
    resetUploadRows();
  };

  // What is on file for the dialog: the chosen applicant's employee documents and the organization's.
  const uploadEmployeeUid = selectedUploadCustomer?.employeeUid || null;
  useEffect(() => {
    let live = true;
    setUploadEmpGroups(null);
    if (!uploadDialogOpen || !uploadEmployeeUid) return undefined;
    loadEmployeeDocGroups(woCustomerKey, uploadEmployeeUid).then(g => { if (live) setUploadEmpGroups(g); });
    return () => { live = false; };
  }, [uploadDialogOpen, woCustomerKey, uploadEmployeeUid]);
  useEffect(() => {
    let live = true;
    setUploadOrgGroups(null);
    if (!uploadDialogOpen) return undefined;
    loadOrgDocGroups(woCustomerType === 'organization' ? woOrgId : null).then(g => { if (live) setUploadOrgGroups(g); });
    return () => { live = false; };
  }, [uploadDialogOpen, woCustomerType, woOrgId]);

  const uploadRows = uploadTypes.map(code => {
    const dt = documentTypes.find(d => (d.code || d.id) === code);
    return { code, label: dt?.label || dt?.name || code, required: false };
  });

  // Pre-fill each new row once from the current version on file (employee types from the employee, company types
  // from the organization); a pre-filled file the user removes stays removed.
  useEffect(() => {
    if (!uploadDialogOpen || uploadOrgGroups === null || (uploadEmployeeUid && uploadEmpGroups === null)) return;
    const tried = new Set(uploadPrefillTried);
    const jobs = uploadRows
      .filter(row => !tried.has(row.code) && !uploadDocs.some(d => d.documentType === row.code))
      .map(row => {
        const kind = prefillKindOf(documentTypes, row.code);
        return { row, kind, group: currentGroupOf(kind === 'org' ? uploadOrgGroups : (uploadEmpGroups || []), row.code) };
      });
    if (!jobs.length) return;
    setUploadPrefillTried(prev => [...prev, ...jobs.map(j => j.row.code)]);
    const customerAtStart = uploadCustomerName;
    const sameCustomer = () => uploadCustomerRef.current === customerAtStart;
    jobs.filter(j => j.group).forEach(j => {
      fetchPrefilledDoc({ kind: j.kind, group: j.group, code: j.row.code, label: j.row.label, customerKey: woCustomerKey, orgId: woOrgId })
        .then(doc => {
          if (!doc || !sameCustomer()) return;
          setUploadDocs(prev => (prev.some(d => d.documentType === j.row.code) ? prev : [...prev, doc]));
        })
        .catch(err => {
          console.error(`[prefill] ${j.row.code}: the file could not be fetched`, err);
          setError(`The ${j.row.label || j.row.code} on file could not be attached - upload it on the row.`);
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadDialogOpen, uploadTypes, uploadDocs, uploadEmpGroups, uploadOrgGroups, uploadEmployeeUid, documentTypes]);

  const addUploadFiles = (files, row) => {
    const { ok, tooBig } = splitBySize(files);
    if (tooBig.length) setError(tooBig.map(oversizeMessage).join(' '));
    if (!ok.length) return;
    const baseId = Date.now();
    setUploadDocs(prev => [...prev, ...ok.map((file, i) => ({
      id: baseId + i + Math.random(), file, fileName: file.name, fileSize: file.size,
      documentType: row.code, documentTypeName: row.label,
    }))]);
  };

  // After WO-record upload, sync the same files to a Thaklis employee under the WO's
  // mapped org so the documents are tracked for expiry. Mirrors the create-WO flow: the applicant's
  // own employee (picked on WO create, employee_uid) when known; older work orders without one fall
  // back to create-or-reuse by passport / Emirates ID. Skips silently when there is no mapped org, or
  // no employee and no id number.
  const syncDetailDocsToThaklis = async (customer, docTypeCode, files) => {
    const mappedOrgId = workOrder?.mapped_organization_id || workOrder?.mappedOrganizationId;
    const passport = customer?.passportNumber?.trim();
    const emiratesId = customer?.emiratesId?.trim();
    const knownUid = customer?.employeeUid || null;
    if (!mappedOrgId || (!knownUid && !passport && !emiratesId) || !files.length) return;
    if (isOrganizationDocType(documentTypes, docTypeCode)) return;

    const customerType = workOrder?.customer_type || workOrder?.customerType || 'organization';
    const internalOrgId = customerType === 'organization'
      ? (workOrder?.organization_id || workOrder?.organizationId || null)
      : null;

    try {
      let uid = knownUid;
      // Thaklis keeps the files under the customer's segment: the client's internal org id, or walkin
      let segment = internalOrgId || 'walkin';
      if (!uid) {
        const resp = await ApiService.createOrReuseEmployee({
          customer_type: customerType,
          mapped_organization_id: mappedOrgId,
          internal_organization_id: internalOrgId,
          name: customer.name || '',
          passport_number: passport || null,
          emirates_id: emiratesId || null
        });
        const result = resp?.data?.data;
        uid = result?.uid;
        segment = result?.segment || segment;
        if (!uid) throw new Error(resp?.data?.error || 'No employee uid returned');
      }

      // OCR each file to capture the document's own number/issue/expiry for tracking.
      // Capture a reason if extraction didn't run (e.g. the TC's OpenAI key isn't configured, or
      // Thaklis skipped it) so it isn't silent — the document itself already uploaded successfully.
      let extractFailure = null;
      const metaList = await Promise.all(files.map(async (f) => {
        try {
          const r = await ApiService.extractDocument(f, 'GENERAL', docTypeCode);
          if (r?.data?.success === false) {
            extractFailure = r.data.error || r.data.message || extractFailure;
            return {};
          }
          const data = r?.data?.data || {};
          if (data._extractionSkipped) {
            extractFailure = data._extractionSkippedReason || 'Automatic field extraction was skipped.';
          }
          return data;
        } catch {
          extractFailure = extractFailure || 'Document extraction failed — the number/expiry could not be read automatically.';
          return {};
        }
      }));
      if (extractFailure) {
        setError(extractFailure);
      }
      const pick = (key) => {
        const hit = metaList.find(m => m && m[key] && String(m[key]).trim() && String(m[key]).toLowerCase() !== 'null');
        return hit ? String(hit[key]).trim() : null;
      };
      // Document number is chosen by the upload's document type (visa -> visaNumber, etc.).
      const pickDocNumber = () => {
        for (const m of metaList) {
          const v = resolveExtractedDocNumber(m, docTypeCode);
          if (v) return v;
        }
        return null;
      };

      await ApiService.uploadEmployeeDocuments(uid, files, docTypeCode, {
        documentNumber: pickDocNumber(),
        issueDate: pick('issueDate'),
        expiryDate: pick('expiryDate')
      }, segment);
    } catch (syncErr) {
      console.error(`Failed to sync documents to Thaklis for ${customer.name}:`, syncErr);
    }
  };


  const handleUploadDocument = async () => {
    if (!uploadDocs.length) {
      setError('Add at least one document');
      return;
    }
    if (woCustomers.length > 0 && !uploadCustomerName) {
      setError('Please select the customer this document belongs to');
      return;
    }

    setUploading(true);
    try {
      const custName = uploadCustomerName || null;
      // 1) Put every staged document on the WO record: a pre-filled company document by its key, a pre-filled employee
      //    document as a WO copy only, new files uploaded (a client's company document goes to its documents first).
      const failed = await submitWorkOrderDocuments({
        workOrderId, docs: uploadDocs, customerName: custName, employeeUid: uploadEmployeeUid,
        documentTypes, description: documentDescription,
      });

      // 2) Track the new, uploaded employee documents against the Thaklis employee (OCR for their number / dates).
      const toSync = new Map();
      uploadDocs.filter(d => !failed.has(d.id) && needsEmployeeSync(documentTypes, d)).forEach(d => {
        if (!toSync.has(d.documentType)) toSync.set(d.documentType, []);
        toSync.get(d.documentType).push(d.file);
      });
      if (toSync.size > 0) {
        setExtractingDocs(true);
        try {
          for (const [code, files] of toSync) {
            await syncDetailDocsToThaklis(selectedUploadCustomer, code, files);
          }
        } finally {
          setExtractingDocs(false);
        }
      }

      const total = uploadDocs.length;
      setUploadDialogOpen(false);
      setUploadCustomerName('');
      setDocumentDescription('');
      resetUploadRows();
      if (failed.size > 0) {
        setError(`${failed.size} of ${total} file(s) failed to upload. Please try uploading them again.`);
      } else {
        setSuccess(total > 1 ? 'Documents uploaded successfully' : 'Document uploaded successfully');
      }
      await fetchWorkOrder();
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Attach deliverable documents to a completed service (WO task). On a provider's delegated-in work
  // order the backend mirrors each file back to the client's linking task (same S3 object).
  const openTaskDocDialog = (task) => {
    setTaskDocTask(task);
    setTaskDocFiles([]);
    setTaskDocTxnNo('');
    setTaskDocUnticked([]);
    setTaskDocType('APPLICATION_FORM');
    setTaskDocTypeOther('');
    setTaskDocError(null);
  };

  const closeTaskDocDialog = () => {
    setTaskDocTask(null);
    setTaskDocFiles([]);
    setTaskDocTxnNo('');
    setTaskDocUnticked([]);
    setTaskDocType('APPLICATION_FORM');
    setTaskDocTypeOther('');
    setTaskDocError(null);
  };

  // Try to auto-fill the transaction number by extracting the application number from the uploaded
  // document(s) via the TC's OpenAI key. Sequential (never parallel): stop on the first found number,
  // stop on the first error (e.g. missing/invalid key), and skip a document that yields nothing.
  const handleExtractTaskDocAppNumber = async () => {
    if (!taskDocFiles.length || !workOrderId) return;
    setTaskDocExtracting(true);
    setTaskDocError(null);
    try {
      for (const file of taskDocFiles) {
        let resp;
        try {
          resp = await ApiService.extractWorkOrderApplicationNumber(workOrderId, file);
        } catch (e) {
          setTaskDocError(e?.response?.data?.message || e?.response?.data?.error || 'Could not extract the application number.');
          return;
        }
        const appNo = resp?.data?.data?.application_number;
        if (appNo) {
          setTaskDocTxnNo(appNo);
          return;
        }
      }
      setTaskDocError('No application number found in the selected document(s).');
    } finally {
      setTaskDocExtracting(false);
    }
  };

  const handleTaskDocUpload = async () => {
    if (!taskDocTask || !taskDocFiles.length) return;
    setTaskDocUploading(true);
    let failed = 0;
    const txnNo = transactionNumberChoices(transactionNumbersOf(taskDocTask), taskDocTxnNo, taskDocUnticked).value;
    // Deliverable doc type: Application Form by default, or a custom name (falling back to "other")
    // when Other is chosen.
    const typeLabel = taskDocType === 'OTHER'
      ? (taskDocTypeOther.trim() || 'other')
      : 'Application Form';
    try {
      for (const file of taskDocFiles) {
        try {
          await ApiService.uploadWorkOrderDocument(
            workOrderId, file, '', typeLabel,
            taskDocTask.customer_name || taskDocTask.customerName || null,
            taskDocTask.id, txnNo);
        } catch (uploadErr) {
          if (txnNo && uploadErr?.response?.status === 400) {
            setTaskDocError(apiErr(uploadErr, 'This transaction number was refused.'));
            return;
          }
          console.error('Error uploading deliverable document:', uploadErr);
          failed++;
        }
      }
      if (failed > 0) {
        setError(`${failed} of ${taskDocFiles.length} file(s) failed to upload. Please try again.`);
      } else {
        setSuccess('Deliverable documents uploaded');
      }
      closeTaskDocDialog();
      await fetchWorkOrder();
    } finally {
      setTaskDocUploading(false);
    }
  };

  // Email a completed service's deliverable documents. Recipients pre-fill from the WO's internal org
  // and customer email; subject/body come from the WORK_ORDER_COMPLETED template (edited in Email Settings).
  const openEmailDialog = async (task) => {
    setEmailTask(task);
    setEmailRecipients('');
    setEmailCc('');
    setEmailLoadingRecipients(true);
    try {
      const resp = await ApiService.getWorkOrderSuggestedRecipients(workOrderId);
      const suggested = resp?.data?.data || [];
      setEmailRecipients(suggested.join(', '));
    } catch (err) {
      console.error('Error loading suggested recipients:', err);
    } finally {
      setEmailLoadingRecipients(false);
    }
  };

  const closeEmailDialog = () => {
    setEmailTask(null);
    setEmailRecipients('');
    setEmailCc('');
  };

  const parseEmails = (s) => (s || '').split(/[,;\s]+/).map(e => e.trim()).filter(e => e.includes('@'));

  const handleSendTaskEmail = async () => {
    if (!emailTask) return;
    const recipients = parseEmails(emailRecipients);
    if (recipients.length === 0) {
      setError('Enter at least one recipient email.');
      return;
    }
    setEmailSending(true);
    try {
      await ApiService.sendWorkOrderTaskEmail(workOrderId, emailTask.id, recipients, parseEmails(emailCc));
      setSuccess('Email sent');
      closeEmailDialog();
    } catch (err) {
      console.error('Error sending deliverable email:', err);
      setError(apiErr(err, 'Failed to send email'));
    } finally {
      setEmailSending(false);
    }
  };

  const handleViewDocument = async (doc) => {
    setPreviewDocument(doc);
    setLoadingPreview(true);
    setPreviewDialogOpen(true);
    try {
      const response = await ApiService.getWorkOrderDocumentDownloadUrl(workOrderId, doc.id);
      const downloadUrl = response?.data?.data?.download_url || response?.data?.data?.downloadUrl;
      if (downloadUrl) {
        setPreviewUrl(downloadUrl);
      }
    } catch (err) {
      console.error('Error getting document URL:', err);
      setError('Failed to load document preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownloadDocument = async (documentId) => {
    try {
      const response = await ApiService.getWorkOrderDocumentDownloadUrl(workOrderId, documentId);
      const downloadUrl = response?.data?.data?.download_url || response?.data?.data?.downloadUrl;
      if (downloadUrl) {
        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = '';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Error getting download URL:', err);
      setError('Failed to download document');
    }
  };

  const getFilePreviewType = (fileName) => {
    if (!fileName) return 'other';
    const ext = fileName.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    if (['md', 'txt', 'json', 'xml', 'csv'].includes(ext)) return 'text';
    return 'other';
  };

  // Ticket linking functions
  const handleOpenLinkTicketDialog = async (task) => {
    setSelectedTask(task);
    setTicketSearchQuery('');
    setLinkTicketDialogOpen(true);

    // Fetch tickets for this typing center (all mapped Thaklis organizations),
    // filtered server-side to the task's exact Thaklis service id so any matching
    // ticket surfaces regardless of recency.
    const typingCenterId = workOrder?.typingCenterId || workOrder?.typing_center_id;
    const taskLineItemId = task.lineItemId || task.line_item_id;
    const lineItem = lineItems.find(li => String(li.id) === String(taskLineItemId));
    const serviceId = lineItem?.service_id || lineItem?.serviceId || null;

    if (typingCenterId) {
      setLoadingTickets(true);
      try {
        const params = { typingCenterId };
        if (serviceId) params.serviceId = serviceId;
        const response = await ApiService.getTickets(params);
        const tickets = response?.data?.data || response?.data || [];
        setOrganizationTickets(tickets);
      } catch (err) {
        console.error('Error fetching tickets:', err);
        setError('Failed to load tickets');
      } finally {
        setLoadingTickets(false);
      }
    }
  };

  const handleLinkTicket = async (ticket) => {
    if (!selectedTask) return;

    setActionLoading(true);
    try {
      // Use orgTicketNumber (with org code) if available, otherwise fall back to ticketNumber
      const ticketNumber = ticket.orgTicketNumber || ticket.org_ticket_number || ticket.ticketNumber || ticket.ticket_number;
      await ApiService.linkTaskToTicket(workOrderId, selectedTask.id, {
        thaklis_ticket_id: ticket.id,
        thaklis_ticket_number: ticketNumber,
        ticket_snapshot: ticket
      });
      setSuccess('Ticket linked successfully');
      setLinkTicketDialogOpen(false);
      setSelectedTask(null);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error linking ticket:', err);
      setError(apiErr(err, 'Failed to link ticket'));
    } finally {
      setActionLoading(false);
    }
  };

  // ---- B2B hand-off ----
  // The two catalogs share no id link, so a provider service is matched to the client line by NAME
  // (case-insensitive, trimmed, inner whitespace collapsed). A match counts only when exactly one
  // provider service shares the name — an ambiguous (>1) or absent match leaves the choice manual.
  const normalizeServiceName = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const clientServiceName = (task, line) =>
    line?.service_name || line?.serviceName || task?.taskName || task?.task_name || '';
  const findB2bServiceMatch = (services, name) => {
    const target = normalizeServiceName(name);
    if (!target) return null;
    const hits = (services || []).filter((s) => normalizeServiceName(s.name) === target);
    return hits.length === 1 ? hits[0] : null;
  };

  // Loads the chosen provider's services and, when the client line's service name matches exactly one
  // of them, auto-selects it. `matchName` is passed on first load (state not yet settled); on a later
  // provider change in the dialog it falls back to the stored b2bMatchName. Returns the service list.
  const loadB2bServicesForConnection = async (connectionId, matchName) => {
    setSelectedB2bConnectionId(connectionId);
    setB2bServices([]);
    setSelectedB2bServiceId(''); // services differ per B2B — clear the prior pick
    if (!connectionId) return [];
    setB2bServicesLoading(true);
    try {
      const resp = await ApiService.getB2bServices(connectionId);
      const list = resp?.data?.data || [];
      setB2bServices(list);
      const match = findB2bServiceMatch(list, matchName !== undefined ? matchName : b2bMatchName);
      if (match) setSelectedB2bServiceId(match.id);
      return list;
    } catch (err) {
      console.error('Error loading B2B services:', err);
      setB2bServices([]);
      setError(apiErr(err, 'Failed to load provider services'));
      return [];
    } finally {
      setB2bServicesLoading(false);
    }
  };

  const handleOpenB2bDialog = async (task) => {
    setSelectedTask(task);
    setSelectedB2bConnectionId('');
    setB2bServices([]);
    setSelectedB2bServiceId('');
    setB2bConfirmOpen(false);
    setB2bAutoMatch(null);
    setB2bFieldValues({});
    setB2bStagedDocs([]);
    setReqDocPick({});
    // Resolve this task's applicant from its line item, then pre-select all of that
    // customer's documents on the WO (matched by customer name).
    const taskLineItemId = task.lineItemId || task.line_item_id;
    const matchedLine = (workOrder?.lineItems || workOrder?.line_items || [])
      .find(li => li.id === taskLineItemId);
    const customerName = matchedLine?.customer_name || matchedLine?.customerName || '';
    const taskCustomerName = customerName.trim().toLowerCase();
    const custDocs = taskCustomerName
      ? (workOrder?.documents || []).filter(
          d => (d.customer_name || d.customerName || '').trim().toLowerCase() === taskCustomerName)
      : [];
    setB2bDocs(custDocs);
    setSelectedDocIds(custDocs.map(d => d.id));
    const matchName = clientServiceName(task, matchedLine);
    setB2bMatchName(matchName);
    try {
      const resp = await ApiService.getB2bPartners();
      const partners = resp?.data?.data || [];
      setB2bPartners(partners);
      // Fast path: a single connected provider with a unique service-name match skips the dialog and
      // asks for a one-tap confirmation instead. Any ambiguity falls through to the full dialog.
      if (partners.length === 1) {
        const list = await loadB2bServicesForConnection(partners[0].connection_id, matchName);
        const match = findB2bServiceMatch(list, matchName);
        if (match) {
          // The confirm fast-path can't collect custom fields or required documents — if the matched
          // service has either, fall through to the full dialog so they can be filled.
          const needsInput = (match.customFields || match.custom_fields || []).length > 0
            || (match.documentRequirements || match.document_requirements || []).length > 0;
          if (!needsInput) {
            setB2bAutoMatch({ service: match, partner: partners[0], customerName, docCount: custDocs.length });
            setB2bConfirmOpen(true);
            return;
          }
        }
      }
      setB2bDialogOpen(true);
    } catch (err) {
      console.error('Error loading B2B partners:', err);
      setB2bPartners([]);
      setError(apiErr(err, 'Failed to load providers'));
      setB2bDialogOpen(true);
    }
  };

  // Switch from the confirmation to the full dialog so the user can change the service or documents;
  // everything (provider, matched service, docs) is already loaded and pre-selected.
  const handleEditB2bFromConfirm = () => {
    setB2bConfirmOpen(false);
    setB2bDialogOpen(true);
  };

  const handleCloseB2bDialog = () => {
    setB2bDialogOpen(false);
    setB2bConfirmOpen(false);
    setB2bAutoMatch(null);
    setSelectedTask(null);
    setB2bServices([]);
    setB2bPartners([]);
    setSelectedB2bConnectionId('');
    setSelectedB2bServiceId('');
    setB2bMatchName('');
    setB2bDocs([]);
    setSelectedDocIds([]);
    setReqDocPick({});
    setB2bFieldValues({});
    setB2bStagedDocs([]);
  };

  const toggleDocId = (docId) => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]);
  };

  // Seed custom-field values from the selected provider service's defaults whenever the pick changes.
  useEffect(() => {
    const svc = b2bServices.find(s => String(s.id) === String(selectedB2bServiceId));
    if (!svc) { setB2bFieldValues({}); return; }
    const seeded = {};
    (svc.customFields || svc.custom_fields || []).forEach(f => {
      const fid = f.fieldId || f.tcCustomFieldId || f.id;
      seeded[fid] = f.orgDefault ?? f.value ?? '';
    });
    setB2bFieldValues(seeded);
  }, [selectedB2bServiceId, b2bServices]);

  const handleB2bFieldChange = (fieldId, value) =>
    setB2bFieldValues(prev => ({ ...prev, [fieldId]: value }));

  // Auto-fill each required document slot from an existing WO doc of a matching type (by code or
  // label, case-insensitive), one doc per slot. Recomputed when the service or the doc set changes.
  useEffect(() => {
    const svc = b2bServices.find(s => String(s.id) === String(selectedB2bServiceId));
    if (!svc) { setReqDocPick({}); return; }
    const reqs = svc.documentRequirements || svc.document_requirements || [];
    const next = {};
    const used = new Set();
    reqs.forEach(r => {
      const match = b2bDocs.find(d => {
        if (used.has(d.id)) return false;
        const t = String(d.document_type || d.documentType || '').trim().toLowerCase();
        return t === String(r.code || '').toLowerCase() || t === String(r.label || '').toLowerCase();
      });
      if (match) { next[r.code] = match.id; used.add(match.id); }
    });
    setReqDocPick(next);
  }, [selectedB2bServiceId, b2bServices, b2bDocs]);

  // Assign / clear an existing WO doc for a required slot (reused, not re-uploaded).
  const pickExistingForReq = (reqCode, docId) =>
    setReqDocPick(prev => ({ ...prev, [reqCode]: docId }));
  const clearReqPick = (reqCode) =>
    setReqDocPick(prev => { const n = { ...prev }; delete n[reqCode]; return n; });

  const stageB2bUpload = (documentType, fileList) => {
    const { ok: files, tooBig } = splitBySize(Array.from(fileList || []));
    if (tooBig.length) setError(tooBig.map(oversizeMessage).join(' '));
    if (files.length === 0) return;
    setB2bStagedDocs(prev => [
      ...prev,
      ...files.map((file, i) => ({ tempId: `${Date.now()}-${i}-${file.name}`, file, documentType, fileName: file.name })),
    ]);
  };

  const removeB2bStaged = (tempId) =>
    setB2bStagedDocs(prev => prev.filter(s => s.tempId !== tempId));

  const handleGiveToB2b = async () => {
    if (!selectedTask || !selectedB2bConnectionId || !selectedB2bServiceId) return;
    const svc = b2bServices.find(s => String(s.id) === String(selectedB2bServiceId));
    const customFields = svc?.customFields || svc?.custom_fields || [];
    const docReqs = svc?.documentRequirements || svc?.document_requirements || [];

    // Hard-block: every required custom field must have a value.
    for (const f of customFields) {
      const fid = f.fieldId || f.tcCustomFieldId || f.id;
      if (f.required && !String(b2bFieldValues[fid] ?? '').trim()) {
        setError(`Please fill the required field "${f.label}" before delegating.`);
        return;
      }
    }
    // Hard-block: every required doc must be satisfied by a reused existing doc or a staged upload.
    for (const r of docReqs) {
      if (r.required && !reqDocPick[r.code] && !b2bStagedDocs.some(s => s.documentType === r.code)) {
        setError(`Please attach the required document "${r.label}" before delegating.`);
        return;
      }
    }

    setB2bSubmitting(true);
    try {
      const fieldValues = customFields
        .map(f => {
          const fid = f.fieldId || f.tcCustomFieldId || f.id;
          return { fieldId: fid, code: f.code, label: f.label, value: b2bFieldValues[fid] };
        })
        .filter(fv => fv.value != null && String(fv.value).trim() !== '');
      // Docs sent to the B2B WO: the reused required-slot docs (re-tagged to the slot's code via
      // documentTypeOverrides) plus the extra docs still ticked in the "other documents" list.
      const assignedIds = new Set(Object.values(reqDocPick));
      const documentTypeOverrides = {};
      Object.entries(reqDocPick).forEach(([code, docId]) => { documentTypeOverrides[docId] = code; });
      const documentIds = [...new Set([
        ...selectedDocIds.filter(id => !assignedIds.has(id)),
        ...Object.values(reqDocPick),
      ])];
      // Selected/reused existing docs are copied to the B2B WO; manually-staged files are uploaded
      // server-side onto the delegated (provider) WO only (no cross-org write, no source-WO copy).
      await ApiService.handoffTaskToB2b(
        workOrderId, selectedTask.id, selectedB2bConnectionId, selectedB2bServiceId,
        documentIds, fieldValues, b2bStagedDocs, documentTypeOverrides);
      setSuccess('Delegated to Provider');
      handleCloseB2bDialog();
      fetchWorkOrder();
    } catch (err) {
      console.error('Error delegating to B2B:', err);
      setError(apiErr(err, 'Failed to delegate to Provider'));
    } finally {
      setB2bSubmitting(false);
    }
  };

  // Note: B2B delegation removal is intentionally not exposed in the UI for now
  // (the /unlink-b2b endpoint + ApiService.unlinkTaskFromB2b remain available).

  const handleUnlinkTicket = async (taskId) => {
    if (!window.confirm('Are you sure you want to unlink this ticket?')) return;

    setActionLoading(true);
    try {
      await ApiService.unlinkTaskFromTicket(workOrderId, taskId);
      setSuccess('Ticket unlinked successfully');
      fetchWorkOrder();
    } catch (err) {
      console.error('Error unlinking ticket:', err);
      setError(apiErr(err, 'Failed to unlink ticket'));
    } finally {
      setActionLoading(false);
    }
  };

  // Employee assignment functions (for internal tasks)
  const handleOpenAssignDialog = async (task) => {
    setSelectedTask(task);
    setEmployeeSearchQuery('');
    setAssignDialogOpen(true);

    // Fetch employees for this typing center
    const typingCenterId = workOrder?.typingCenterId || workOrder?.typing_center_id;
    if (typingCenterId) {
      setLoadingEmployees(true);
      try {
        const response = await ApiService.getTypingCenterUsers(typingCenterId);
        const users = response?.data?.data || response?.data || [];
        setEmployees(users);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    }
  };

  const handleAssignTask = async (employee) => {
    if (!selectedTask) return;

    // Assign the internal task (TTask) created from this WO task
    const internalTask = createdTasks[selectedTask.id];
    if (!internalTask) {
      setError('Please create an internal task first before assigning');
      return;
    }

    setActionLoading(true);
    try {
      await TaskService.assign(
        internalTask.id,
        employee.id,
        employee.fullName || employee.full_name || employee.name
      );
      setSuccess('Task assigned successfully');
      setAssignDialogOpen(false);
      setSelectedTask(null);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error assigning task:', err);
      setError(apiErr(err, 'Failed to assign task'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnassignTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to unassign this task?')) return;

    // Unassign the internal task (TTask) created from this WO task
    const internalTask = createdTasks[taskId];
    if (!internalTask) return;

    setActionLoading(true);
    try {
      await TaskService.unassign(internalTask.id);
      setSuccess('Task unassigned successfully');
      fetchWorkOrder();
    } catch (err) {
      console.error('Error unassigning task:', err);
      setError(apiErr(err, 'Failed to unassign task'));
    } finally {
      setActionLoading(false);
    }
  };

  // Create internal task (generates task number via TaskService)
  const handleCreateInternalTask = async (task) => {
    setCreatingTask(task.id);
    try {
      // Derive the applicant name from the task's own line item (multi-applicant
      // WOs don't carry a single header customer_name).
      const taskLineItemId = task.lineItemId || task.line_item_id;
      const matchedLine = (workOrder?.lineItems || workOrder?.line_items || [])
        .find(li => li.id === taskLineItemId);
      const taskCustomerName =
        matchedLine?.customer_name || matchedLine?.customerName || '';
      const newTask = await TaskService.create({
        taskName: task.taskName || task.task_name,
        description: task.description || '',
        workOrderId: workOrderId,
        workOrderNumber: workOrder?.work_order_number || workOrder?.workOrderNumber,
        workOrderTaskId: task.id,
        // Organization (company)
        organizationId: workOrder?.organizationId || workOrder?.organization_id,
        organizationName: workOrder?.organization_name || workOrder?.organizationName,
        // Customer (actual applicant for this specific line item)
        customerName: taskCustomerName
      });

      // Store the created task number
      setCreatedTasks(prev => ({
        ...prev,
        [task.id]: newTask
      }));

      setSuccess(`Task ${newTask.taskNumber} created successfully`);
    } catch (err) {
      console.error('Error creating internal task:', err);
      setError(apiErr(err, 'Failed to create internal task'));
    } finally {
      setCreatingTask(null);
    }
  };

  // The id travels as a query param so the deep link survives a refresh or a copied URL.
  const handleViewInternalTask = (taskId) => {
    const createdTask = createdTasks[taskId];
    if (!createdTask) return;
    navigate(createdTask.id ? `/tasks?taskId=${encodeURIComponent(createdTask.id)}` : '/tasks');
  };

  // Open Work Order Assignment Dialog
  const handleOpenWoAssignDialog = async () => {
    setEmployeeSearchQuery('');
    setWoAssignDialogOpen(true);

    // Fetch employees for this typing center
    const typingCenterId = workOrder?.typingCenterId || workOrder?.typing_center_id;
    if (typingCenterId) {
      setLoadingEmployees(true);
      try {
        const response = await ApiService.getTypingCenterUsers(typingCenterId);
        const users = response?.data?.data || response?.data || [];
        setEmployees(users);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setEmployees([]);
      } finally {
        setLoadingEmployees(false);
      }
    }
  };

  // Assign entire Work Order to employee (auto-assigns internal tasks)
  const handleAssignWorkOrder = async (employee) => {
    setAssigningWorkOrder(true);
    try {
      const assignmentData = {
        assigned_to: employee.id,
        assigned_to_name: employee.fullName || employee.full_name || employee.name
      };

      // Backend assigns all internal tasks (TTasks) linked to WO tasks
      await ApiService.assignWorkOrder(workOrderId, assignmentData);

      setSuccess(`All internal tasks assigned to ${assignmentData.assigned_to_name}`);
      setWoAssignDialogOpen(false);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error assigning work order:', err);
      setError(apiErr(err, 'Failed to assign work order'));
    } finally {
      setAssigningWorkOrder(false);
    }
  };

  // Unassign Work Order (also unassigns internal tasks)
  const handleUnassignWorkOrder = async () => {
    if (!window.confirm('Are you sure you want to unassign this work order? This will also unassign all internal tasks.')) return;

    setAssigningWorkOrder(true);
    try {
      // Backend unassigns all internal tasks (TTasks) linked to WO tasks
      await ApiService.unassignWorkOrder(workOrderId);

      setSuccess('All internal tasks unassigned');
      fetchWorkOrder();
    } catch (err) {
      console.error('Error unassigning work order:', err);
      setError(apiErr(err, 'Failed to unassign work order'));
    } finally {
      setAssigningWorkOrder(false);
    }
  };

  // Upload docs staged in the Add/Update Service dialog onto the WO, grouped by type, then run the
  // same Thaklis employee tracking the manual upload uses (OCR + expiry reminders) per group.
  const uploadStagedServiceDocs = async (stagedDocs, customerName) => {
    if (!stagedDocs?.length) return;
    const customerObj = getWoCustomers().find(c => c.name === customerName) || null;
    const byCode = new Map();
    stagedDocs.forEach(s => {
      if (!byCode.has(s.code)) byCode.set(s.code, { label: s.label, files: [] });
      byCode.get(s.code).files.push(s.file);
    });
    for (const [code, grp] of byCode) {
      const uploaded = [];
      for (const file of grp.files) {
        try {
          // Send the label as document type (matches the manual upload path), plus the type code and the
          // applicant's employee uid (company documents too - they are uploaded under the applicant).
          await ApiService.uploadWorkOrderDocument(workOrderId, file, '', grp.label || code, customerName || null, null, null,
            customerObj?.employeeUid || null, code);
          uploaded.push(file);
        } catch (uploadErr) {
          console.error('Error uploading service document to work order:', uploadErr);
        }
      }
      if (uploaded.length > 0) {
        try { await syncDetailDocsToThaklis(customerObj, code, uploaded); }
        catch (syncErr) { console.error('Thaklis document sync failed:', syncErr); }
      }
    }
  };

  // The line's applicant's Thaklis employee (from the WO's lines), so a new or changed line keeps it.
  const withEmployeeUid = (serviceData) => ({
    ...serviceData,
    employee_uid: getWoCustomers().find(c => c.name === serviceData.customer_name)?.employeeUid || null
  });

  const handleAddLineItem = async (serviceData, stagedDocs = []) => {
    setAddingService(true);
    try {
      await ApiService.addWorkOrderLineItem(workOrderId, withEmployeeUid(serviceData));
      await uploadStagedServiceDocs(stagedDocs, serviceData.customer_name);
      setSuccess('Service added successfully');
      setAddServiceDialogOpen(false);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error adding service:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to add service');
    } finally {
      setAddingService(false);
    }
  };

  const handleUpdateLineItem = async (serviceData, stagedDocs = []) => {
    if (!editingLineItem) return;
    setUpdatingService(true);
    try {
      await ApiService.updateWorkOrderLineItem(workOrderId, editingLineItem.id, withEmployeeUid(serviceData));
      await uploadStagedServiceDocs(stagedDocs, serviceData.customer_name);
      setSuccess('Service updated successfully');
      setEditingLineItem(null);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error updating service:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to update service');
    } finally {
      setUpdatingService(false);
    }
  };

  const handleRemoveLineItem = async (lineItemId, confirmed = false) => {
    const paid = paidOutcomePayments(lineItemId);
    if (!confirmed) {
      if (paid.length > 0) { setOutcomes({}); setRemoveLineTarget(lineItemId); return; }
      if (!window.confirm('Remove this service? The associated task will also be removed.')) return;
    }
    setActionLoading(true);
    try {
      await workOrderMoneyService.removeLine(workOrderId, lineItemId, paid.length ? outcomesBody(paid, outcomes) : undefined);
      setRemoveLineTarget(null);
      setOutcomes({});
      setSuccess('Service removed successfully');
      fetchWorkOrder();
      loadMoney();
    } catch (err) {
      console.error('Error removing service:', err);
      setError(serverMessage(err, 'Failed to remove service'));
    } finally {
      setActionLoading(false);
    }
  };

  // Line-item markup handlers — mirrors the invoice/quotation detail pages.
  // Input is markup-including-tax; split via LINE_ITEM_VAT_RATE (5%).
  const openLineItemMarkupDialog = (lineItem) => {
    setSelectedLineItem(lineItem);
    const existingInclTax = (parseFloat(lineItem.markup_amount || lineItem.markupAmount) || 0)
      + (parseFloat(lineItem.tax_on_markup || lineItem.taxOnMarkup) || 0);
    setLineItemMarkupAmount(existingInclTax > 0 ? String(existingInclTax) : '');
    setMarkupDialogOpen(true);
  };

  const closeLineItemMarkupDialog = () => {
    setMarkupDialogOpen(false);
    setSelectedLineItem(null);
    setLineItemMarkupAmount('');
  };

  const handleApplyLineItemMarkup = async () => {
    if (!selectedLineItem || !lineItemMarkupAmount || parseFloat(lineItemMarkupAmount) <= 0) {
      setError('Please enter a valid markup amount');
      return;
    }
    const markupInclTax = parseFloat(lineItemMarkupAmount);
    const markupExclTax = Number((markupInclTax / (1 + LINE_ITEM_VAT_RATE)).toFixed(2));
    const taxOnMarkup = Number((markupInclTax - markupExclTax).toFixed(2));

    setApplyingLineItemMarkup(true);
    try {
      await ApiService.applyWorkOrderLineItemMarkup(selectedLineItem.id, {
        markupAmount: markupExclTax,
        taxOnMarkup
      });
      setSuccess('Markup applied');
      closeLineItemMarkupDialog();
      fetchWorkOrder();
    } catch (err) {
      console.error('Error applying markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to apply markup');
    } finally {
      setApplyingLineItemMarkup(false);
    }
  };

  const handleRemoveLineItemMarkup = async (lineItemId) => {
    if (!window.confirm('Remove markup from this service?')) return;
    try {
      await ApiService.removeWorkOrderLineItemMarkup(lineItemId);
      setSuccess('Markup removed');
      fetchWorkOrder();
    } catch (err) {
      console.error('Error removing markup:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to remove markup');
    }
  };

  const filteredEmployees = employees.filter(emp => {
    if (!employeeSearchQuery) return true;
    const query = employeeSearchQuery.toLowerCase();
    const name = (emp.fullName || emp.full_name || emp.name || '').toLowerCase();
    const role = (emp.role || emp.position || '').toLowerCase();
    return name.includes(query) || role.includes(query);
  });

  // Get list of already linked ticket IDs and numbers from current work order tasks
  const linkedTicketIds = (workOrder?.tasks || [])
    .filter(task => task.thaklisTicketId || task.thaklis_ticket_id)
    .map(task => String(task.thaklisTicketId || task.thaklis_ticket_id));

  const linkedTicketNumbers = (workOrder?.tasks || [])
    .filter(task => task.thaklisTicketNumber || task.thaklis_ticket_number)
    .map(task => String(task.thaklisTicketNumber || task.thaklis_ticket_number || '').toUpperCase());

  const filteredTickets = organizationTickets.filter(ticket => {
    // Service matching is done server-side by serviceId — here only the free-text search.
    if (!ticketSearchQuery) return true;
    const query = ticketSearchQuery.toLowerCase();
    const ticketNum = (ticket.ticketNumber || ticket.ticket_number || '').toLowerCase();
    const ticketServiceName = (ticket.serviceName || ticket.service_name || ticket.service?.name || '').toLowerCase();
    const customerName = (ticket.customerName || ticket.customer_name || ticket.applicantName || ticket.applicant_name || '').toLowerCase();
    return ticketNum.includes(query) || ticketServiceName.includes(query) || customerName.includes(query);
  });

  // Helper to check if ticket is already linked (by ID or ticket number)
  const isTicketLinked = (ticket) => {
    const ticketId = String(ticket.id || '');
    const ticketNum = String(ticket.orgTicketNumber || ticket.org_ticket_number ||
                       ticket.ticketNumber || ticket.ticket_number || '').toUpperCase();
    return linkedTicketIds.includes(ticketId) || linkedTicketNumbers.includes(ticketNum);
  };

  // Helper to get customer/applicant name
  const getTicketPersonName = (ticket) => {
    return ticket.customerName || ticket.customer_name ||
           ticket.applicantName || ticket.applicant_name ||
           ticket.applicant?.name || ticket.customer?.name ||
           ticket.employeeName || ticket.employee_name || 'N/A';
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    setActionLoading(true);
    try {
      await ApiService.deleteWorkOrderDocument(workOrderId, documentId);
      fetchWorkOrder();
    } catch (err) {
      console.error('Error deleting document:', err);
      setError('Failed to delete document');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!workOrder) return;

    // KAN-242 (D8): a completed work order bills all its lines, a partly invoiced one the lines left
    const partly = workOrder.status === 'PARTIALLY_INVOICED';
    if (!window.confirm(partly ? 'The remaining lines will be invoiced.' : 'All work order lines will be invoiced.')) {
      return;
    }

    // Load the typing center's mapped organizations. If exactly one exists, skip the
    // dialog and submit directly. Otherwise show the picker.
    setLoadingMappedOrgs(true);
    try {
      const tcId = workOrder.typingCenterId || workOrder.typing_center_id;
      const mappingsResponse = await ApiService.get(`/api/mappings?typingCenterId=${tcId}`);
      const orgs = (mappingsResponse.data?.data || []).map(m => ({
        id: m.organization_id,
        name: m.organization_name,
        code: m.organization_code || ''
      }));
      setMappedOrgOptions(orgs);

      if (orgs.length === 0) {
        setError('No mapped organization found for this typing center. Cannot create invoice.');
        return;
      }

      // Default from the WO's stored mapped org (the org its applicants were tracked under).
      // When it's valid in the TC's current mappings, use it directly so the invoice inherits
      // the same mapped org — no re-selection needed.
      const woMappedId = workOrder.mapped_organization_id || workOrder.mappedOrganizationId;
      const woMappedOrg = woMappedId ? orgs.find(o => o.id === woMappedId) : null;
      if (woMappedOrg) {
        await submitCreateInvoice(woMappedOrg);
        return;
      }

      if (orgs.length === 1) {
        await submitCreateInvoice(orgs[0]);
        return;
      }
      // No WO mapped org (or it's no longer a valid mapping) — let the user pick.
      setSelectedMappedOrg(null);
      setCreateInvoiceDialogOpen(true);
    } catch (err) {
      console.error('Error loading mapped organizations:', err);
      setError('Failed to load mapped organizations');
    } finally {
      setLoadingMappedOrgs(false);
    }
  };

  const submitCreateInvoice = async (mappedOrg) => {
    if (!mappedOrg) return;
    setActionLoading(true);
    try {
      const response = await ApiService.createInvoiceFromWorkOrder(workOrderId, {
        organizationId: mappedOrg.id,
        organizationName: mappedOrg.name,
        organizationCode: mappedOrg.code
      });
      const newInvoice = response?.data?.data || response?.data;

      if (newInvoice?.id) {
        setSuccess('Invoice created successfully');
        setCreateInvoiceDialogOpen(false);
        navigate(`/invoices/${newInvoice.id}`);
      } else {
        setError('Failed to create invoice');
      }
    } catch (err) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      OPEN: { color: 'info', label: 'Open' },
      IN_PROGRESS: { color: 'warning', label: 'In Progress' },
      ON_HOLD: { color: 'warning', label: 'On Hold', variant: 'outlined' },
      COMPLETED: { color: 'success', label: 'Completed' },
      PARTIALLY_INVOICED: { color: 'info', label: 'Partly invoiced' },
      CANCELLED: { color: 'error', label: 'Cancelled' },
      INVOICED: { color: 'secondary', label: 'Invoiced' }
    };
    const config = statusConfig[status] || { color: 'info', label: status, variant: 'outlined' };
    return <Chip label={config.label} color={config.color} variant={config.variant} size="medium" />;
  };

  const getTaskStatusChip = (status) => {
    const statusConfig = {
      PENDING: { color: 'info', label: 'Pending', variant: 'outlined' },
      IN_PROGRESS: { color: 'warning', label: 'In Progress' },
      COMPLETED: { color: 'success', label: 'Completed' }
    };
    const config = statusConfig[status] || { color: 'info', label: status, variant: 'outlined' };
    return <Chip label={config.label} color={config.color} variant={config.variant} size="small" />;
  };

  const getDocumentTypeLabel = (value) => {
    // Backend now stores the Thaklis-provided label directly (e.g. "Emirates ID Copy").
    // For legacy records that stored a code, resolve via the fetched types list.
    if (!value) return '—';
    const found = documentTypes.find(dt =>
      dt.label === value || dt.name === value || dt.code === value || dt.id === value);
    if (found) return found.label || found.name || value;
    return value;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!workOrder) {
    return (
      <Box>
        <Alert severity="error">Work order not found</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/work-orders')} sx={{ mt: 2 }}>
          Back to Work Orders
        </Button>
      </Box>
    );
  }

  const tasks = workOrder.tasks || [];
  const documents = workOrder.documents || [];
  const lineItems = workOrder.lineItems || workOrder.line_items || [];
  const status = workOrder.status;
  // Write actions follow the permission matrix (TC_ADMIN/platform resolve all-true).
  const canWriteWo = can(currentUser, 'work_order', 'write');
  const canCreateInvoice = can(currentUser, 'invoice', 'write');
  // Deliverable documents linked to a specific service (WO task), keyed by task id.
  const docTaskId = (d) => d.work_order_task_id || d.workOrderTaskId || null;
  const docsForTask = (taskId) => documents.filter(d => docTaskId(d) === taskId);
  const emailTaskDocs = emailTask ? docsForTask(emailTask.id) : [];
  // Service (task) name shown as a highlight chip on task-linked documents.
  const taskNameById = {};
  tasks.forEach(t => { taskNameById[t.id] = t.taskName || t.task_name; });
  const taskDocExisting = transactionNumbersOf(taskDocTask);
  const taskDocChoices = transactionNumberChoices(taskDocExisting, taskDocTxnNo, taskDocUnticked);
  // Services (line items) can be edited while the WO is still pending review or in progress.
  const canEditServices = (status === 'IN_PROGRESS' || status === 'PENDING_REVIEW') && canWriteWo;
  const progressPercent = workOrder.progress_percent || workOrder.progressPercent || 0;
  const totalTasks = workOrder.total_tasks || workOrder.totalTasks || 0;
  const completedTasks = workOrder.completed_tasks || workOrder.completedTasks || 0;

  // The Tasks / Services / Documents sections. Each takes its list, so the same JSX (with every row
  // action) renders the whole work order in the "All" view and one customer's subset in a customer tab.
  const renderTasksSection = (tasks) => (
    <>
          {/* Tasks */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                Tasks
              </Typography>
              {status === 'IN_PROGRESS' && canWriteWo && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setTaskDialogOpen(true)}
                  sx={{ color: 'secondary.main', borderColor: 'primary.main', '&:hover': { borderColor: 'primary.dark', bgcolor: 'action.hover' } }}
                >
                  Add Task
                </Button>
              )}
            </Box>

            {tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                No tasks yet
              </Typography>
            ) : (
              <List disablePadding>
                {tasks.map((task) => {
                  const hasTicketLink = task.thaklisTicketId || task.thaklis_ticket_id;
                  // Internal-task info comes from the child TTask (createdTasks map), not the WO task.
                  const internalTask = createdTasks[task.id];
                  const isAssigned = !!(internalTask && internalTask.assignedTo);
                  const assignedName = internalTask?.assignedToName;

                  // Task type comes straight from the backend's is_thaklis_task flag
                  // (derived from the line item's is_custom_service at WO creation);
                  // a linked ticket also implies a Thaklis task.
                  const backendThaklisFlag = task.is_thaklis_task !== undefined
                    ? task.is_thaklis_task
                    : task.isThaklisTask;
                  const isThaklisTask = hasTicketLink || backendThaklisFlag === true;
                  const isInternalTask = !isThaklisTask;

                  const taskCompleted = task.status === 'COMPLETED';
                  const taskTxnNumbers = transactionNumbersOf(task);
                  const woInProgress = status === 'IN_PROGRESS';
                  const ticketId = task.thaklisTicketId || task.thaklis_ticket_id;
                  const ticketStatus = ticketId ? ticketStatuses[ticketId] : null;
                  // B2B delegation for a non-Thaklis line on a promoted TC.
                  const b2bWoNumber = task.b2bWorkOrderNumber || task.b2b_work_order_number;
                  const delegatedToB2b = Boolean(task.b2bWorkOrderId || task.b2b_work_order_id);
                  const b2bStatus = b2bWoStatuses[task.id] || null;
                  // Delegation is per line item (it carries the applicant + price into the B2B WO),
                  // so a manually-added task with no line item can't be given to a B2B.
                  const hasLineItem = Boolean(task.lineItemId || task.line_item_id);
                  // Deliverable documents attached to this completed service.
                  const taskDocCount = docsForTask(task.id).length;
                  const canUploadDeliverable = taskCompleted && hasLineItem && status !== 'CANCELLED';

                  return (
                    <ListItem
                      key={task.id}
                      sx={{
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 'none' },
                        py: 0.5,
                        px: 2
                      }}
                      secondaryAction={
                        <Box display="flex" alignItems="center" gap={1}>
                          {getTaskStatusChip(task.status)}

                          {/* Completed service: attach the deliverable document(s). Green once some exist;
                              hover shows the count. Allowed even after the WO itself is completed. */}
                          {canUploadDeliverable && (
                            <Tooltip title={taskDocCount > 0
                              ? `${taskDocCount} document${taskDocCount === 1 ? '' : 's'} — add more`
                              : 'Upload deliverable documents'}>
                              <IconButton
                                size="small"
                                onClick={() => openTaskDocDialog(task)}
                                sx={{ color: taskDocCount > 0 ? 'success.main' : 'secondary.main' }}
                              >
                                <UploadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Email the deliverable documents to the client. */}
                          {canUploadDeliverable && (
                            <Tooltip title={taskDocCount > 0
                              ? 'Email these documents to the client'
                              : 'Upload documents first to email them'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openEmailDialog(task)}
                                  disabled={taskDocCount === 0}
                                  sx={{ color: 'info.main' }}
                                >
                                  <EmailIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Completed task: only a Revert action so linking / internal task can be corrected */}
                          {woInProgress && taskCompleted && (
                            <Tooltip title="Revert to Pending">
                              <IconButton size="small" color="warning" onClick={() => handleReopenTask(task.id)}>
                                <RevertIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {/* Active actions — only while the work order is IN_PROGRESS and the task isn't completed */}
                          {woInProgress && !taskCompleted && (
                            <>
                              {isThaklisTask && (
                                hasTicketLink ? (
                                  <Tooltip title="Unlink Thaklis Ticket">
                                    <IconButton size="small" onClick={() => handleUnlinkTicket(task.id)} sx={{ color: 'error.main' }}>
                                      <LinkOffIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Link to Thaklis Ticket">
                                    <IconButton size="small" onClick={() => handleOpenLinkTicketDialog(task)} sx={{ color: 'info.main' }}>
                                      <LinkIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )
                              )}

                              {isInternalTask && (
                                delegatedToB2b ? null : (
                                  <>
                                    {internalTask ? (
                                      // Task exists — always show Assign; green tick + name tooltip once assigned; click reassigns.
                                      <Tooltip title={isAssigned ? `Assigned to ${assignedName} — click to reassign` : 'Assign to Employee'}>
                                        <IconButton size="small" onClick={() => handleOpenAssignDialog(task)} sx={{ color: isAssigned ? 'success.main' : 'secondary.main' }}>
                                          {isAssigned ? <AssignedIcon fontSize="small" /> : <AssignIcon fontSize="small" />}
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Create Internal Task">
                                        <IconButton size="small" onClick={() => handleCreateInternalTask(task)} disabled={creatingTask === task.id} sx={{ color: 'secondary.main' }}>
                                          {creatingTask === task.id ? (
                                            <CircularProgress size={18} sx={{ color: 'secondary.main' }} />
                                          ) : (
                                            <CreateTaskIcon fontSize="small" />
                                          )}
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {/* Promoted TC: alternatively delegate this line to the super-B2B (only when no internal task yet). */}
                                    {isPromoted && !internalTask && hasLineItem && (
                                      <Tooltip title="Give to Provider">
                                        <IconButton size="small" onClick={() => handleOpenB2bDialog(task)} sx={{ color: 'info.main' }}>
                                          <B2bIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                )
                              )}

                              <Tooltip title="Mark Complete">
                                <IconButton size="small" color="success" onClick={() => handleTaskComplete(task)}>
                                  <CompleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Delete Task">
                                <IconButton size="small" sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }} onClick={() => handleDeleteTask(task.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {isThaklisTask ? (
                          <TicketIcon sx={{ color: hasTicketLink ? 'info.main' : 'text.disabled', fontSize: 20 }} />
                        ) : (
                          <InternalTaskIcon sx={{ color: isAssigned ? 'primary.main' : 'text.disabled', fontSize: 20 }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{ sx: { fontSize: '0.8125rem' } }}
                        primary={
                          <>
                            {task.taskName || task.task_name}
                            {task.description && (
                              <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 1 }}>
                                {task.description}
                              </Typography>
                            )}
                          </>
                        }
                        secondary={
                          <>
                            {/* Thaklis Task: Show linked ticket or prompt */}
                            {isThaklisTask && (
                              hasTicketLink ? (
                                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    icon={<LinkIcon sx={{ fontSize: 14 }} />}
                                    label={task.thaklisTicketNumber || task.thaklis_ticket_number}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onDelete={woInProgress && !taskCompleted ? () => handleUnlinkTicket(task.id) : undefined}
                                    sx={{ fontSize: '0.7rem', height: 22 }}
                                  />
                                  {ticketStatus && (
                                    <Chip
                                      label={ticketStatus}
                                      size="small"
                                      color={ticketStatus === 'COMPLETED' ? 'success' : 'default'}
                                      variant={ticketStatus === 'COMPLETED' ? 'filled' : 'outlined'}
                                      sx={{ fontSize: '0.65rem', height: 20 }}
                                    />
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="caption" component="span" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                  Not linked to Thaklis ticket
                                </Typography>
                              )
                            )}
                            {/* Delegated to B2B: show the B2B work-order number + its live status */}
                            {isInternalTask && delegatedToB2b && (
                              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                <Chip
                                  icon={<B2bIcon sx={{ fontSize: 14 }} />}
                                  label={b2bWoNumber}
                                  size="small"
                                  color="info"
                                  variant="outlined"
                                  sx={{ fontSize: '0.7rem', height: 22 }}
                                />
                                {b2bStatus && (
                                  <Chip
                                    label={b2bStatus}
                                    size="small"
                                    color={b2bStatus === 'COMPLETED' ? 'success' : 'default'}
                                    variant={b2bStatus === 'COMPLETED' ? 'filled' : 'outlined'}
                                    sx={{ fontSize: '0.65rem', height: 20 }}
                                  />
                                )}
                              </Box>
                            )}
                            {/* Internal Task: Show task number if created, then assigned employee */}
                            {isInternalTask && !delegatedToB2b && (
                              <>
                                {createdTasks[task.id] ? (
                                  <Chip
                                    icon={<InternalTaskIcon sx={{ fontSize: 14 }} />}
                                    label={createdTasks[task.id].taskNumber}
                                    size="small"
                                    clickable
                                    onClick={() => handleViewInternalTask(task.id)}
                                    sx={{
                                      mt: 0.5,
                                      mr: 0.5,
                                      fontSize: '0.7rem',
                                      height: 22,
                                      bgcolor: 'action.selected',
                                      color: 'info.main',
                                      borderColor: 'info.light',
                                      '&:hover': { bgcolor: 'action.focus' }
                                    }}
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="caption" component="span" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                    No task created yet
                                  </Typography>
                                )}
                                {internalTask && isAssigned && (
                                  <Chip
                                    icon={<AssignIcon sx={{ fontSize: 14 }} />}
                                    label={assignedName}
                                    size="small"
                                    sx={{
                                      mt: 0.5,
                                      fontSize: '0.7rem',
                                      height: 22,
                                      bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.15)' : '#e8f5e9',
                                      color: 'success.dark',
                                      borderColor: 'success.light',
                                      '& .MuiChip-deleteIcon': { color: 'success.dark' }
                                    }}
                                    variant="outlined"
                                    onDelete={woInProgress && !taskCompleted ? () => handleUnassignTask(task.id) : undefined}
                                  />
                                )}
                              </>
                            )}
                            {taskTxnNumbers.length > 0 && (
                              <Box component="span" sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                {taskTxnNumbers.map((number) => (
                                  <Chip key={number} label={`Txn: ${number}`} size="small" variant="outlined"
                                    sx={{ fontSize: '0.65rem', height: 20 }} />
                                ))}
                              </Box>
                            )}
                          </>
                        }
                        primaryTypographyProps={{
                          fontSize: '0.85rem',
                          fontWeight: task.status === 'COMPLETED' ? 400 : 500,
                          color: task.status === 'COMPLETED' ? 'text.secondary' : 'text.primary'
                        }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>

    </>
  );

  const renderServicesSection = (lineItems) => (
    <>
          {/* Line Items */}
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                Services
              </Typography>
              {canEditServices && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddServiceDialogOpen(true)}
                  sx={{ color: 'secondary.main', borderColor: 'primary.main', '&:hover': { borderColor: 'primary.dark', bgcolor: 'action.hover' } }}
                >
                  Add Service
                </Button>
              )}
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }}>Service</TableCell>
                    <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }} align="right">Service Fee</TableCell>
                    <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }} align="right">Govt Fee</TableCell>
                    <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }} align="right">Markup</TableCell>
                    <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }} align="right">Tax</TableCell>
                    <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }} align="right">Total</TableCell>
                    <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }} align="center">Payment</TableCell>
                    {canEditServices && (
                      <TableCell sx={{ bgcolor: isDark ? 'grey.800' : 'primary.main', color: 'primary.contrastText', fontWeight: 600, fontSize: '0.8rem', py: 1 }} align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item) => {
                    const itemMarkup = parseFloat(item.markup_amount || item.markupAmount || 0);
                    const itemTaxOnMarkup = parseFloat(item.tax_on_markup || item.taxOnMarkup || 0);
                    // The WO task for this line (if any). A completed task blocks updating the service.
                    const woTaskForItem = tasks.find(t => String(t.lineItemId || t.line_item_id) === String(item.id));
                    const itemTaskCompleted = Boolean(woTaskForItem && String(woTaskForItem.status || '').toUpperCase() === 'COMPLETED');
                    // Only internal-service line items are updatable; Thaklis services are not editable.
                    const itemIsInternal = Boolean(item.is_custom_service ?? item.isCustomService);
                    const fieldValues = item.field_values || item.fieldValues || [];
                    return (
                    <React.Fragment key={item.id}>
                    <TableRow>
                      <TableCell sx={{ fontSize: '0.8125rem', py: 1 }}>
                        <Typography variant="body2" >{item.service_name || item.serviceName}</Typography>
                        {(item.customer_name || item.customerName) && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            {item.customer_name || item.customerName}
                          </Typography>
                        )}
                        {/* Per-item invoice (INVOICE-BY-ITEM.md §5.4): each line's live invoice */}
                        {item.invoice?.number ? (
                          <Chip size="small" color="success" label={`Invoiced · ${item.invoice.number}`}
                            sx={{ mt: 0.25, height: 18, fontSize: '0.65rem' }} />
                        ) : ['COMPLETED', 'PARTIALLY_INVOICED', 'INVOICED'].includes(workOrder?.status) && (
                          <Chip size="small" variant="outlined" label="Not invoiced"
                            sx={{ mt: 0.25, height: 18, fontSize: '0.65rem' }} />
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>{formatCurrency(item.service_charge || item.serviceCharge)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>{formatCurrency(item.govt_charge || item.govtCharge)}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                          {itemMarkup > 0 ? (
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
                              {formatCurrency(itemMarkup)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
                          )}
                          {canEditServices && (
                            itemMarkup > 0 ? (
                              <>
                                <Tooltip title="Edit markup">
                                  <IconButton size="small" onClick={() => openLineItemMarkupDialog(item)}>
                                    <EditIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove markup">
                                  <IconButton size="small" sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }} onClick={() => handleRemoveLineItemMarkup(item.id)}>
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <Tooltip title="Add markup">
                                <IconButton size="small" color="success" onClick={() => openLineItemMarkupDialog(item)}>
                                  <AddIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>
                        <Typography variant="body2">{formatCurrency(item.tax_value || item.taxValue)}</Typography>
                        {itemTaxOnMarkup > 0 && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'success.main' }}>
                            (+{formatCurrency(itemTaxOnMarkup)})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8125rem', py: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{formatCurrency(item.line_total || item.lineTotal)}</Typography>
                        {itemMarkup > 0 && (
                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                            (Base: {formatCurrency(item.original_line_total || item.originalLineTotal)})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {!(item.is_custom_service ?? item.isCustomService) ? (
                          <Tooltip title="Thaklis pays this service's government fee">
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          </Tooltip>
                        ) : (() => {
                          const toPay = lineItemsToPay(item, workOrder?.tasks, linePayments, lineAddons);
                          const unpaid = toPay.filter((x) => !x.payment).length;
                          // evidence is optional: a pass-through whose evidence was started still needs its receipt (or was rejected)
                          const evidenceNeeded = toPay.some((x) => x.passThrough && x.payment
                            && x.payment.source !== 'CUSTOMER_CARD' && x.payment.source !== 'PARTNER'
                            && Boolean(x.evidence?.authType || x.evidence?.receiptRef || x.evidence?.hasReceipt)
                            && !['ACCEPTED', 'PENDING_REVIEW'].includes(x.evidence?.status));
                          const title = toPay.length === 0 ? 'Payment & Invoice Settings'
                            : unpaid > 0 ? 'Set payment source (required)'
                              : evidenceNeeded ? 'Evidence needed — attach the receipt' : 'Paid — Payment & Invoice Settings';
                          const color = unpaid > 0 ? 'warning.main' : evidenceNeeded ? 'error.main' : toPay.length ? 'success.main' : 'text.secondary';
                          return (
                            <Tooltip title={title}>
                              <IconButton size="small" sx={{ color }}
                                onClick={() => setPaymentItemId(item.id)}>
                                {unpaid > 0 ? <WarningIcon sx={{ fontSize: 18 }} /> : <PaymentIcon sx={{ fontSize: 18 }} />}
                              </IconButton>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      {canEditServices && (
                        <TableCell align="center" sx={{ py: 1 }}>
                          {itemIsInternal && (
                            <Tooltip title={itemTaskCompleted ? 'Reopen the task to update this service' : 'Update Service'}>
                              <span>
                                <IconButton size="small" disabled={itemTaskCompleted} onClick={() => setEditingLineItem(item)} sx={{ color: 'primary.main' }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Remove Service">
                            <IconButton size="small" onClick={() => handleRemoveLineItem(item.id)} sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                    {fieldValues.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={canEditServices ? 8 : 7} sx={{ py: 0.75, px: 2, bgcolor: 'action.hover', borderBottom: '2px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {fieldValues.map((fv, i) => (
                              <Chip
                                key={i}
                                size="small"
                                label={`${fv.label || fv.code || 'Field'}: ${fv.value}`}
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

    </>
  );

  const renderDocumentsSection = (documents) => (
    <>
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Documents</Typography>
              {status !== 'CANCELLED' && canWriteWo && (
                <Button variant="contained" size="small" startIcon={<UploadIcon />} onClick={openUploadDialog}
                  sx={{ fontSize: '0.75rem', textTransform: 'none' }}>Upload</Button>
              )}
            </Box>
            {documents.length === 0 ? (
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', textAlign: 'center', py: 2 }}>
                No documents uploaded yet
              </Typography>
            ) : (
              <Box>
                {documents.map((doc) => {
                  const docType = (doc.documentType || doc.document_type || '').toUpperCase().replace(/\s+/g, '_');
                  const prettyType = getDocumentTypeLabel(doc.documentType || doc.document_type);
                  return (
                    <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                      <Typography component="span" sx={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{getDocIcon(docType)}</Typography>
                      <Typography noWrap sx={{ fontSize: '0.75rem', fontWeight: 500, width: 130, flexShrink: 0 }}>{prettyType}</Typography>
                      <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', flex: 1, minWidth: 0 }}>
                        {doc.fileName || doc.file_name}
                        {' · '}{formatFileSize(doc.fileSize || doc.file_size)}
                        {(doc.customer_name || doc.customerName) ? ` · ${doc.customer_name || doc.customerName}` : ''}
                      </Typography>
                      {/* Which service the document was delivered for, and provider origin. */}
                      {docTaskId(doc) && taskNameById[docTaskId(doc)] && (
                        <Chip label={taskNameById[docTaskId(doc)]} size="small" color="info" variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18, maxWidth: 160, flexShrink: 1 }} />
                      )}
                      {(doc.source_document_id || doc.sourceDocumentId) && (
                        <Chip icon={<B2bIcon sx={{ fontSize: 12 }} />} label="From provider" size="small" variant="outlined"
                          sx={{ fontSize: '0.65rem', height: 18, flexShrink: 0 }} />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0, justifyContent: 'flex-end' }}>
                        <IconButton size="small" onClick={() => handleViewDocument(doc)} sx={{ color: 'text.secondary' }}>
                          <ViewIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDownloadDocument(doc.id)} sx={{ color: 'text.secondary' }}>
                          <DownloadIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        {canWriteWo && (
                          <IconButton size="small" onClick={() => handleDeleteDocument(doc.id)}
                            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Paper>
    </>
  );

  // ---- Customer-wise view ----
  // Customers are the distinct applicant names on the line items (plus any named only on documents).
  // A task belongs to its line's customer; documents carry the customer name. Anything without a
  // customer (manual tasks, untagged docs) goes to a "General" tab.
  const custKey = (name) => String(name || '').trim().toLowerCase();
  const lineById = {};
  lineItems.forEach(li => { lineById[li.id] = li; });
  const lineCustomer = (li) => (li ? (li.customer_name || li.customerName || '') : '');
  const taskCustomer = (t) => lineCustomer(lineById[t.lineItemId || t.line_item_id]);
  const docCustomer = (d) => d.customer_name || d.customerName || '';

  const customerGroups = [];
  const groupByKey = {};
  const ensureGroup = (name) => {
    const key = custKey(name);
    if (!groupByKey[key]) {
      groupByKey[key] = { key, name: String(name).trim(), passportNumber: '', emiratesId: '', tasks: [], lineItems: [], documents: [] };
      customerGroups.push(groupByKey[key]);
    }
    return groupByKey[key];
  };
  const generalGroup = { key: '__general', name: 'General', tasks: [], lineItems: [], documents: [] };
  lineItems.forEach(li => {
    const name = lineCustomer(li);
    if (!name.trim()) { generalGroup.lineItems.push(li); return; }
    const g = ensureGroup(name);
    g.passportNumber = g.passportNumber || li.customer_id_number || li.customerIdNumber || '';
    g.emiratesId = g.emiratesId || li.customer_emirates_id || li.customerEmiratesId || '';
    g.lineItems.push(li);
  });
  tasks.forEach(t => {
    const name = taskCustomer(t);
    (name.trim() ? ensureGroup(name) : generalGroup).tasks.push(t);
  });
  documents.forEach(d => {
    const name = docCustomer(d);
    (name.trim() ? ensureGroup(name) : generalGroup).documents.push(d);
  });
  const hasGeneral = generalGroup.tasks.length + generalGroup.lineItems.length + generalGroup.documents.length > 0;
  const customerTabs = [...customerGroups, ...(hasGeneral ? [generalGroup] : [])];
  const ADD_CUSTOMER_TAB = '__add';
  const activeCustomerKey = customerTab === ADD_CUSTOMER_TAB && canEditServices
    ? ADD_CUSTOMER_TAB
    : (customerTabs.find(g => g.key === customerTab)?.key || customerTabs[0]?.key || (canEditServices ? ADD_CUSTOMER_TAB : null));
  const activeGroup = customerTabs.find(g => g.key === activeCustomerKey) || null;

  const changeDetailView = (view) => {
    if (!view) return;
    setDetailView(view);
    try { localStorage.setItem('woDetailView', view); } catch (e) { /* the view choice is a convenience only */ }
  };

  const groupTotal = (group) => group.lineItems.reduce(
    (sum, li) => sum + (Number(li.line_total ?? li.lineTotal) || 0), 0);
  const formatAed = (amount) =>
    new Intl.NumberFormat('en-AE', { style: 'currency', currency: 'AED' }).format(amount || 0);

  const renderWorkSections = () => {
    const toggle = (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={detailView} onChange={(e, v) => changeDetailView(v)}>
          <ToggleButton value="customer" sx={{ textTransform: 'none', px: 2 }}>By customer</ToggleButton>
          <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>All</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    );

    if (detailView !== 'customer') {
      return (
        <>
          {toggle}
          {renderTasksSection(tasks)}
          {renderServicesSection(lineItems)}
          {renderDocumentsSection(documents)}
        </>
      );
    }

    return (
      <>
        {toggle}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeCustomerKey || false}
            onChange={(e, v) => setCustomerTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
          >
            {customerTabs.map(g => (
              <Tab
                key={g.key}
                value={g.key}
                label={`${g.name} (${g.lineItems.length})`}
                sx={{ textTransform: 'none', fontStyle: g.key === '__general' ? 'italic' : 'normal' }}
              />
            ))}
            {canEditServices && (
              <Tab
                value={ADD_CUSTOMER_TAB}
                icon={<AddIcon sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label="Add customer"
                sx={{ textTransform: 'none', color: 'secondary.main' }}
              />
            )}
          </Tabs>
          {activeGroup && (
            <Box sx={{ px: 3, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 600 }}>{activeGroup.name}</Typography>
              {activeGroup.passportNumber && <Chip size="small" label={`Passport ${activeGroup.passportNumber}`} sx={{ fontSize: '0.7rem' }} />}
              {activeGroup.emiratesId && <Chip size="small" label={`EID ${activeGroup.emiratesId}`} sx={{ fontSize: '0.7rem' }} />}
              <Box sx={{ flex: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {activeGroup.lineItems.length} service{activeGroup.lineItems.length === 1 ? '' : 's'} · {formatAed(groupTotal(activeGroup))}
              </Typography>
            </Box>
          )}
        </Paper>

        {activeCustomerKey === ADD_CUSTOMER_TAB ? (
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: '0.875rem', mb: 2 }}>Add a customer to this work order</Typography>
            <AddWorkOrderCustomerPanel
              workOrder={workOrder}
              currentUser={currentUser}
              documentTypes={documentTypes}
              showThaklisServices={showThaklisServices}
              existingCustomerNames={customerGroups.map(g => g.name)}
              onCancel={() => setCustomerTab(customerTabs[0]?.key || null)}
              onAdded={async ({ name, warning }) => {
                await fetchWorkOrder();
                setCustomerTab(custKey(name));
                setSuccess(`${name} added to the work order`);
                if (warning) setError(`${name} was added, but ${warning}.`);
              }}
            />
          </Paper>
        ) : activeGroup ? (
          <>
            {renderTasksSection(activeGroup.tasks)}
            {renderServicesSection(activeGroup.lineItems)}
            {renderDocumentsSection(activeGroup.documents)}
          </>
        ) : (
          <Paper sx={{ p: 2.5, mb: 2 }}>
            <Typography color="text.secondary" textAlign="center">No customers on this work order yet.</Typography>
          </Paper>
        )}
      </>
    );
  };

  return (
    <Box sx={{ pb: isMobile ? 2 : 0 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2} flexWrap="wrap">
        <IconButton size="small" onClick={() => navigate('/work-orders')}>
          <BackIcon fontSize="small" />
        </IconButton>
        <Box flex={1}>
          <Box display="flex" alignItems="baseline" gap={1.5} flexWrap="wrap">
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
              {workOrder.work_order_number || workOrder.workOrderNumber}
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {workOrder.organization_name || workOrder.organizationName}
            </Typography>
            {workOrder.title && (
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                — {workOrder.title}
              </Typography>
            )}
          </Box>
        </Box>
        {getStatusChip(status)}
      </Box>



      {/* Errors (extraction, task-completion guards, etc.) surface as a toast so they're seen even
          when the action is far down the page. */}
      <Snackbar
        open={!!error}
        onClose={(e, reason) => { if (reason !== 'clickaway') setError(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ maxWidth: 480 }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Full-page block during document upload + extraction — nothing else is interactable until done. */}
      <Backdrop open={uploading} sx={{ color: '#fff', zIndex: (t) => t.zIndex.modal + 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography>{extractingDocs ? 'Extracting document details… please wait' : 'Uploading… please wait'}</Typography>
        </Box>
      </Backdrop>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Progress + Action Buttons — one row */}
      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {/* Progress left */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, flexShrink: 0 }}>
              Progress
            </Typography>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant="determinate" value={progressPercent}
                sx={{ height: 6, borderRadius: 3 }} />
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', flexShrink: 0 }}>
              {completedTasks}/{totalTasks} · {progressPercent}%
            </Typography>
          </Box>
          {/* Task Assignment (in progress only) */}
          {status === 'IN_PROGRESS' && canWriteWo && (
            <Box display="flex" gap={0.5} flexShrink={0}>
              <Button size="small" variant="outlined" startIcon={assigningWorkOrder ? <CircularProgress size={14} color="inherit" /> : <AssignIcon sx={{ fontSize: 14 }} />}
                onClick={handleOpenWoAssignDialog} disabled={assigningWorkOrder}
                sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}>
                Assign
              </Button>
              <Button size="small" variant="outlined" onClick={handleUnassignWorkOrder} disabled={assigningWorkOrder}
                sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25, color: 'text.secondary', borderColor: 'divider' }}>
                Unassign
              </Button>
            </Box>
          )}
          {/* Status Actions */}
          <Box display="flex" gap={0.5} flexShrink={0}>
          {status === 'PENDING_REVIEW' && canWriteWo && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<StartIcon />}
              onClick={() => handleStatusAction('approve')}
              disabled={actionLoading}
            >
              Approve & Open
            </Button>
          )}
          {status === 'OPEN' && canWriteWo && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<StartIcon />}
              onClick={() => handleStatusAction('start')}
              disabled={actionLoading}
            >
              Start Work
            </Button>
          )}
          {status === 'IN_PROGRESS' && canWriteWo && (
            <>
              <Button
                variant="outlined"
                startIcon={<HoldIcon />}
                onClick={() => handleStatusAction('hold')}
                disabled={actionLoading}
                sx={{ color: 'warning.main', borderColor: 'warning.main', '&:hover': { borderColor: 'warning.dark', bgcolor: 'action.hover' } }}
              >
                Put On Hold
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CompleteIcon />}
                onClick={() => handleStatusAction('complete')}
                disabled={actionLoading}
              >
                Complete
              </Button>
            </>
          )}
          {status === 'ON_HOLD' && canWriteWo && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<StartIcon />}
              onClick={() => handleStatusAction('start')}
              disabled={actionLoading}
            >
              Resume Work
            </Button>
          )}
          {(status === 'COMPLETED' || status === 'PARTIALLY_INVOICED') && canCreateInvoice && (
            <Button
              variant="contained"
              color="secondary"
              startIcon={actionLoading ? <CircularProgress size={20} color="inherit" /> : <InvoiceIcon />}
              onClick={handleCreateInvoice}
              disabled={actionLoading}
            >
              {actionLoading ? 'Creating...' : 'Invoice this work order'}
            </Button>
          )}
          {(status === 'PENDING_REVIEW' || status === 'OPEN' || status === 'IN_PROGRESS' || status === 'ON_HOLD') && canWriteWo && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/work-orders/${workOrderId}/edit`)}
              sx={{ color: 'secondary.main', borderColor: 'primary.main' }}
            >
              Edit
            </Button>
          )}
          {(status === 'PENDING_REVIEW' || status === 'OPEN' || status === 'IN_PROGRESS' || status === 'ON_HOLD') && can(currentUser, 'work_order', 'delete') && (
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={() => setCancelDialogOpen(true)}
              disabled={actionLoading}
              sx={{ color: 'error.main', borderColor: 'error.main', '&:hover': { borderColor: 'error.dark', bgcolor: 'action.hover' } }}
            >
              Cancel
            </Button>
          )}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {/* Left Column - Details */}
        <Grid item xs={12} lg={8}>

          {renderWorkSections()}
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ position: 'sticky', top: 20 }}>
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography variant="h6" fontWeight={600} mb={1.5} sx={{ fontSize: '0.875rem' }}>
                Summary
              </Typography>

              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Order Date</Typography>
                  <Typography variant="body2">{formatDate(workOrder.order_date || workOrder.orderDate)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Expected Completion</Typography>
                  <Typography variant="body2">{formatDate(workOrder.expected_completion_date || workOrder.expectedCompletionDate)}</Typography>
                </Box>
                {(workOrder.quotation_number || workOrder.quotationNumber) && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">From Quotation</Typography>
                    <Typography variant="body2" color="primary" fontWeight={500}>
                      {workOrder.quotation_number || workOrder.quotationNumber}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                  <Typography variant="body2">{formatCurrency(workOrder.subtotal)}</Typography>
                </Box>
                {(() => {
                  const byCustomerCard = (component, lineId, addonId) => linePayments.some((p) => p.status === 'RECORDED'
                    && p.source === 'CUSTOMER_CARD' && p.component === component && p.lineItemId === lineId && (!addonId || p.addonId === addonId));
                  const billedAddons = lineAddons.filter((a) => !byCustomerCard('ADDON', a.lineItemId, a.id));
                  const billedFines = (workOrder.lineItems || []).filter((l) => l.fine_amount != null
                    && l.fine_liability === 'CUSTOMER_LIABILITY' && !byCustomerCard('FINE', l.id));
                  const addonSum = billedAddons.reduce((t, a) => t + Number(a.amount || 0), 0);
                  const addonVat = billedAddons.reduce((t, a) => t + Number(a.vatAmount || 0), 0);
                  const fineSum = billedFines.reduce((t, l) => t + Number(l.fine_amount || 0), 0);
                  return (
                    <>
                      {billedAddons.length > 0 && (
                        <Box display="flex" justifyContent="space-between" pl={1.5}>
                          <Typography variant="caption" color="text.secondary">incl. Add-ons ({billedAddons.length})</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(addonSum)}{addonVat > 0 ? ` + VAT ${formatCurrency(addonVat)}` : ''}
                          </Typography>
                        </Box>
                      )}
                      {billedFines.length > 0 && (
                        <Box display="flex" justifyContent="space-between" pl={1.5}>
                          <Typography variant="caption" color="text.secondary">incl. Fines ({billedFines.length})</Typography>
                          <Typography variant="caption" color="text.secondary">{formatCurrency(fineSum)}</Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">VAT (5%)</Typography>
                  <Typography variant="body2">{formatCurrency(workOrder.tax_total || workOrder.taxTotal)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" fontWeight={600}>Grand Total</Typography>
                  <Typography variant="body2" fontWeight={700} color="primary" fontSize="1rem">
                    {formatCurrency(workOrder.grand_total || workOrder.grandTotal)}
                  </Typography>
                </Box>
              </Stack>

              {(workOrder.notes || workOrder.description) && (
                <>
                  <Divider sx={{ my: 2 }} />
                  {workOrder.description && (
                    <Box mb={2}>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Description
                      </Typography>
                      <Typography variant="body2">{workOrder.description}</Typography>
                    </Box>
                  )}
                  {workOrder.notes && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        Notes
                      </Typography>
                      <Typography variant="body2">{workOrder.notes}</Typography>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Task Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Task</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Task Name"
              value={newTaskName}
              onChange={(e) => setNewTaskName(e.target.value)}
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddTask}
            disabled={!newTaskName.trim() || actionLoading}
            sx={{
              '&:disabled': { bgcolor: 'primary.light', color: '#fff' }
            }}
          >
            Add Task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {woCustomers.length > 0 && (
              <FormControl fullWidth size="small" required>
                <InputLabel sx={{ fontSize: '0.8125rem' }}>Customer</InputLabel>
                <Select
                  value={uploadCustomerName}
                  label="Customer"
                  onChange={(e) => changeUploadCustomer(e.target.value)}
                  sx={{ fontSize: '0.8125rem' }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  {woCustomers.map((c) => {
                    const idTag = c.passportNumber || c.emiratesId;
                    return (
                      <MenuItem key={c.name} value={c.name} sx={{ fontSize: '0.8125rem' }}>
                        {c.name}
                        {idTag && (
                          <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.disabled', ml: 1 }}>
                            {idTag}
                          </Typography>
                        )}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
            {selectedUploadCustomer && !selectedUploadCustomer.employeeUid
              && !selectedUploadCustomer.passportNumber?.trim() && !selectedUploadCustomer.emiratesId?.trim() && (
              <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                No unique identifier for this applicant. Add a Passport Number or Emirates ID —
                without one, the employee and their documents can't be tracked, so you won't get
                expiry reminders for their documents.
              </Alert>
            )}
            {loadingDocTypes ? (
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Loading document types...</Typography>
            ) : documentTypes.length === 0 ? (
              <Alert severity="error" sx={{ fontSize: '0.75rem' }}>
                Document types could not be loaded, so documents cannot be uploaded right now.
              </Alert>
            ) : (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <WorkOrderDocumentRows
                  rows={uploadRows}
                  documents={uploadDocs}
                  documentTypes={documentTypes}
                  onAddType={(code) => code && setUploadTypes(prev => (prev.includes(code) ? prev : [...prev, code]))}
                  onFiles={addUploadFiles}
                  onRemove={(docId) => setUploadDocs(prev => prev.filter(d => d.id !== docId))}
                  captureName={uploadCustomerName}
                  disabled={!uploadCustomerChosen}
                />
              </Box>
            )}
            <TextField
              fullWidth
              size="small"
              label="Description"
              value={documentDescription}
              onChange={(e) => setDocumentDescription(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadDocument}
            disabled={!uploadDocs.length || uploading || !uploadCustomerChosen}
            startIcon={uploading ? <CircularProgress size={20} /> : null}
            sx={{
              '&:disabled': { bgcolor: 'primary.light', color: '#fff' }
            }}
          >
            {uploading ? (extractingDocs ? 'Extracting…' : 'Uploading…') : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocking full-page loader — covers the whole upload chain
          (WO document uploads → Thaklis tracking → refresh). */}
      <Backdrop
        open={uploading}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff' }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Deliverable documents for a completed service (WO task) */}
      <Dialog open={Boolean(taskDocTask)} onClose={taskDocUploading ? undefined : closeTaskDocDialog} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1.5 }}>
          <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (t) => alpha(t.palette.success.main, 0.14), color: 'success.main' }}>
            <UploadIcon fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Upload deliverables</Typography>
            {taskDocTask && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {taskDocTask.taskName || taskDocTask.task_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={closeTaskDocDialog} size="small" disabled={taskDocUploading} sx={{ mt: -0.5, mr: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              component="label"
              sx={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 0.5, py: 2.5, px: 2, textAlign: 'center', cursor: taskDocUploading ? 'default' : 'pointer',
                border: '1.5px dashed', borderColor: 'divider', borderRadius: 2, color: 'text.secondary',
                transition: 'border-color .15s, background-color .15s',
                '&:hover': taskDocUploading ? {} : { borderColor: 'success.main', bgcolor: 'action.hover' },
              }}
            >
              <UploadIcon sx={{ color: 'success.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {taskDocFiles.length ? 'Add more files' : 'Drop files here or browse'}
              </Typography>
              <Typography variant="caption" color="text.secondary">PDF, JPG or PNG</Typography>
              <input type="file" hidden multiple disabled={taskDocUploading}
                onChange={(e) => {
                  const { ok, tooBig } = splitBySize(Array.from(e.target.files || []));
                  if (tooBig.length) setError(tooBig.map(oversizeMessage).join(' '));
                  setTaskDocFiles((prev) => [...prev, ...ok]);
                }} />
            </Box>

            {taskDocFiles.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {taskDocFiles.map((f, idx) => (
                  <Box key={`${f.name}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>{f.name}</Typography>
                    <IconButton size="small" disabled={taskDocUploading} onClick={() => setTaskDocFiles((prev) => prev.filter((_, i) => i !== idx))} sx={{ p: 0.25 }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.75, display: 'block' }}>Document type</Typography>
              <ToggleButtonGroup value={taskDocType} exclusive size="small" fullWidth disabled={taskDocUploading}
                onChange={(e, v) => v && setTaskDocType(v)}>
                <ToggleButton value="APPLICATION_FORM" sx={{ textTransform: 'none' }}>Application Form</ToggleButton>
                <ToggleButton value="OTHER" sx={{ textTransform: 'none' }}>Other</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {taskDocType === 'OTHER' && (
              <TextField fullWidth size="small" label="Document type name" placeholder={'Leave blank to save as "other"'}
                value={taskDocTypeOther} onChange={(e) => setTaskDocTypeOther(e.target.value)} disabled={taskDocUploading} />
            )}

            <TextField
              fullWidth size="small" label="Transaction number"
              value={taskDocTxnNo}
              onChange={(e) => { setTaskDocTxnNo(e.target.value); setTaskDocError(null); }}
              disabled={taskDocUploading}
              error={Boolean(taskDocError)}
              helperText={taskDocError || 'Optional — or pull it from the document'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button size="small" onClick={handleExtractTaskDocAppNumber}
                      disabled={!taskDocFiles.length || taskDocExtracting || taskDocUploading}
                      startIcon={taskDocExtracting ? <CircularProgress size={14} color="inherit" /> : <ExtractIcon sx={{ fontSize: 16, color: '#D4A017' }} />}
                      sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                      {taskDocExtracting ? 'Reading…' : 'Extract'}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            <TransactionNumberChoices
              existing={taskDocExisting}
              choices={taskDocChoices}
              unticked={taskDocUnticked}
              disabled={taskDocUploading}
              onToggle={(key) => setTaskDocUnticked((prev) => (
                prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
              ))}
            />
            <Typography variant="caption" color="text.secondary">
              These are attached to this service and shown in Documents, tagged with the service name.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleTaskDocUpload}
            disabled={!taskDocFiles.length || taskDocUploading || taskDocChoices.tooMany}
            startIcon={taskDocUploading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', minWidth: 120, borderRadius: 2 }}
          >
            {taskDocUploading ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email a completed service's deliverable documents to the client */}
      <Dialog open={Boolean(emailTask)} onClose={emailSending ? undefined : closeEmailDialog} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1.5 }}>
          <Box sx={{ flexShrink: 0, width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: (t) => alpha(t.palette.info.main, 0.12), color: 'info.main' }}>
            <EmailIcon fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>Email documents</Typography>
            {emailTask && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {emailTask.taskName || emailTask.task_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={closeEmailDialog} size="small" disabled={emailSending} sx={{ mt: -0.5, mr: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="To"
              placeholder="name@example.com, another@example.com"
              value={emailRecipients}
              onChange={(e) => setEmailRecipients(e.target.value)}
              disabled={emailSending || emailLoadingRecipients}
              helperText={emailLoadingRecipients ? 'Loading suggested recipients…' : 'Separate multiple addresses with commas'}
            />
            <TextField
              fullWidth
              size="small"
              label="Cc (optional)"
              value={emailCc}
              onChange={(e) => setEmailCc(e.target.value)}
              disabled={emailSending}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                Attachments ({emailTaskDocs.length})
              </Typography>
              {emailTaskDocs.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No documents linked to this service yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {emailTaskDocs.map((d) => (
                    <Box key={d.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <FileIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                      <Typography variant="caption" noWrap>{d.fileName || d.file_name}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              The subject and message come from your Work Order Completed email template (Email Settings).
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleSendTaskEmail}
            disabled={emailSending || emailLoadingRecipients || emailTaskDocs.length === 0}
            startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon fontSize="small" />}
            sx={{ textTransform: 'none', minWidth: 120, borderRadius: 2 }}
          >
            {emailSending ? 'Sending…' : 'Send email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Work Order</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography color="text.secondary" mb={2}>
              Are you sure you want to cancel this work order? This action cannot be undone.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Cancellation Reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              multiline
              rows={2}
            />
            {paidOutcomePayments().length > 0 && (
              <Box mt={2}>
                <Typography fontWeight={600} color="primary.main" mb={1}>Paid items — what happened to the money?</Typography>
                <PaidItemOutcomes payments={paidOutcomePayments()} outcomes={outcomes} setOutcomes={setOutcomes} accounts={tcBankAccounts} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Work Order</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleStatusAction('cancel')}
            disabled={actionLoading || !outcomesComplete(paidOutcomePayments(), outcomes)}
          >
            Cancel Work Order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(removeLineTarget)} onClose={() => setRemoveLineTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Service</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" mb={2}>
            This service has paid items. Say what happened to each payment before removing it; its task is removed too.
          </Typography>
          {removeLineTarget && (
            <PaidItemOutcomes payments={paidOutcomePayments(removeLineTarget)} outcomes={outcomes} setOutcomes={setOutcomes} accounts={tcBankAccounts} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveLineTarget(null)}>Keep Service</Button>
          <Button variant="contained" color="error"
            disabled={actionLoading || !removeLineTarget || !outcomesComplete(paidOutcomePayments(removeLineTarget), outcomes)}
            onClick={() => handleRemoveLineItem(removeLineTarget, true)}>
            Remove Service
          </Button>
        </DialogActions>
      </Dialog>

      <WorkOrderPaymentDialog
        open={Boolean(paymentItemId)}
        onClose={() => setPaymentItemId(null)}
        workOrder={workOrder}
        line={(workOrder?.lineItems || []).find((i) => i.id === paymentItemId)}
        tasks={workOrder?.tasks}
        payments={linePayments}
        addons={lineAddons}
        onChanged={async () => { await loadMoney(); fetchWorkOrder(true); }}
        onSaved={setSuccess}
      />

      {/* Link Ticket Dialog */}
      <Dialog open={linkTicketDialogOpen} onClose={() => setLinkTicketDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="span">Link Thaklis Ticket</Typography>
            {selectedTask && (
              <Typography variant="body2" color="text.secondary">
                Task: {selectedTask.taskName || selectedTask.task_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setLinkTicketDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tickets by number or service..."
              value={ticketSearchQuery}
              onChange={(e) => setTicketSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ mb: 2 }}
            />

            {loadingTickets ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={30} />
              </Box>
            ) : filteredTickets.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography color="text.secondary">
                  No matching tickets found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Looking for tickets with service: {selectedTask?.taskName || selectedTask?.task_name}
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredTickets.map((ticket) => {
                  const isLinked = isTicketLinked(ticket);
                  return (
                    <ListItem
                      key={ticket.id}
                      button
                      onClick={() => !isLinked && handleLinkTicket(ticket)}
                      disabled={isLinked}
                      sx={{
                        border: '1px solid',
                        borderColor: isLinked ? 'warning.light' : 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: isLinked ? 'action.hover' : 'transparent',
                        opacity: isLinked ? 0.8 : 1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography variant="body2" fontWeight={600} color={isLinked ? 'text.secondary' : 'primary'}>
                              {ticket.orgTicketNumber || ticket.org_ticket_number || ticket.ticketNumber || ticket.ticket_number}
                            </Typography>
                            <Chip
                              label={ticket.status}
                              size="small"
                              sx={{
                                fontSize: '0.65rem',
                                height: 18,
                                bgcolor: ticket.status === 'COMPLETED' ? (theme) => theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.15)' : '#e8f5e9' :
                                         ticket.status === 'OPEN' ? 'action.selected' : 'action.hover',
                                color: ticket.status === 'COMPLETED' ? 'success.dark' :
                                       ticket.status === 'OPEN' ? 'info.main' : 'warning.dark'
                              }}
                            />
                            {isLinked && (
                              <Chip
                                label="Already Linked"
                                size="small"
                                sx={{
                                  fontSize: '0.65rem',
                                  height: 18,
                                  bgcolor: 'warning.light',
                                  color: 'warning.dark'
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              <strong>Service:</strong> {ticket.serviceName || ticket.service_name || ticket.service?.name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" component="div" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              <strong>Customer/Employee:</strong> {getTicketPersonName(ticket)}
                            </Typography>
                          </Box>
                        }
                        primaryTypographyProps={{ fontSize: '0.85rem' }}
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Fast-path confirmation — single provider + a unique service-name match, shown as a hand-off slip */}
      <Dialog
        open={b2bConfirmOpen}
        onClose={handleCloseB2bDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1.5 }}>
          <Box
            sx={{
              flexShrink: 0, width: 40, height: 40, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: (t) => alpha(t.palette.info.main, 0.12), color: 'info.main',
            }}
          >
            <B2bIcon fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Delegate to provider
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Matched automatically from your catalog.
            </Typography>
          </Box>
          <IconButton onClick={handleCloseB2bDialog} size="small" sx={{ mt: -0.5, mr: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {b2bAutoMatch && (() => {
            const svc = b2bAutoMatch.service;
            const total = Number(svc.service_charge || 0) + Number(svc.govt_charge || 0) + Number(svc.tax_on_service_charge || 0);
            const Row = ({ label, children, last }) => (
              <Box
                sx={{
                  display: 'grid', gridTemplateColumns: '84px 1fr', columnGap: 1.5, alignItems: 'center',
                  py: 1.25, ...(last ? {} : { borderBottom: (t) => `1px solid ${alpha(t.palette.info.main, 0.12)}` }),
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{label}</Typography>
                <Box sx={{ minWidth: 0 }}>{children}</Box>
              </Box>
            );
            return (
              <Box
                sx={{
                  borderRadius: 2, px: 2, py: 0.5,
                  bgcolor: (t) => alpha(t.palette.info.main, 0.06),
                  border: (t) => `1px solid ${alpha(t.palette.info.main, 0.18)}`,
                }}
              >
                <Row label="Provider">
                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                    {b2bAutoMatch.partner.b2b_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{b2bAutoMatch.partner.b2b_code}</Typography>
                </Row>
                <Row label="Service">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{svc.name}</Typography>
                    <Chip
                      label={`${total.toFixed(2)} AED`}
                      size="small"
                      sx={{ fontWeight: 600, bgcolor: (t) => alpha(t.palette.info.main, 0.14), color: 'info.main' }}
                    />
                  </Box>
                </Row>
                {b2bAutoMatch.customerName && (
                  <Row label="Applicant">
                    <Typography variant="body2" noWrap>{b2bAutoMatch.customerName}</Typography>
                  </Row>
                )}
                <Row label="Documents" last>
                  <Typography variant="body2">
                    {b2bAutoMatch.docCount === 0
                      ? 'No documents'
                      : `All ${b2bAutoMatch.docCount} document${b2bAutoMatch.docCount === 1 ? '' : 's'}`}
                  </Typography>
                </Row>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={handleEditB2bFromConfirm} disabled={b2bSubmitting} sx={{ textTransform: 'none', color: 'text.secondary' }}>Change</Button>
          <Button
            variant="contained"
            onClick={handleGiveToB2b}
            disabled={b2bSubmitting || !selectedB2bConnectionId || !selectedB2bServiceId}
            startIcon={b2bSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', minWidth: 128, borderRadius: 2 }}
          >
            {b2bSubmitting ? 'Delegating…' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Give to B2B Dialog — pick a mapped B2B service (priced for this client) to delegate the line */}
      <Dialog
        open={b2bDialogOpen}
        onClose={handleCloseB2bDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 1.5 }}>
          <Box
            sx={{
              flexShrink: 0, width: 40, height: 40, borderRadius: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: (t) => alpha(t.palette.info.main, 0.12), color: 'info.main',
            }}
          >
            <B2bIcon fontSize="small" />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Give to provider
            </Typography>
            {selectedTask && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {selectedTask.taskName || selectedTask.task_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseB2bDialog} size="small" sx={{ mt: -0.5, mr: -0.5 }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
          {b2bPartners.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              You have no connected providers. Connect with a provider from Settings → Connections first.
            </Typography>
          ) : (
            <>
              {b2bPartners.length > 1 && (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={selectedB2bConnectionId}
                    label="Provider"
                    onChange={(e) => loadB2bServicesForConnection(e.target.value)}
                  >
                    {b2bPartners.map((p) => (
                      <MenuItem key={p.connection_id} value={p.connection_id}>
                        {p.b2b_name} ({p.b2b_code})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {!selectedB2bConnectionId ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Select a provider to see its services.</Typography>
              ) : b2bServicesLoading ? (
                <Box display="flex" justifyContent="center" py={3}><CircularProgress size={30} /></Box>
              ) : b2bServices.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No services are available from this provider for you. Ask them to map a service and price.
                </Typography>
              ) : (
            <>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>Choose a service</Typography>
            <Stack spacing={1} sx={{ maxHeight: 280, overflow: 'auto', mb: 1, pr: 0.5 }}>
              {b2bServices.map((svc) => {
                const sc = Number(svc.service_charge || 0);
                const gc = Number(svc.govt_charge || 0);
                const tax = Number(svc.tax_on_service_charge || 0);
                const total = sc + gc + tax;
                const picked = String(svc.id) === String(selectedB2bServiceId);
                return (
                  <Box
                    key={svc.id}
                    onClick={() => !b2bSubmitting && setSelectedB2bServiceId(svc.id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2,
                      cursor: b2bSubmitting ? 'default' : 'pointer',
                      border: '1px solid',
                      borderColor: picked ? 'primary.main' : 'divider',
                      bgcolor: (t) => picked ? alpha(t.palette.primary.main, 0.06) : 'transparent',
                      transition: 'border-color .15s, background-color .15s',
                      '&:hover': { borderColor: picked ? 'primary.main' : (t) => alpha(t.palette.primary.main, 0.5) },
                    }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{svc.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Service {sc.toFixed(2)} · Govt {gc.toFixed(2)} · Tax {tax.toFixed(2)}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${total.toFixed(2)} AED`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        bgcolor: (t) => alpha((picked ? t.palette.primary : t.palette.info).main, 0.14),
                        color: picked ? 'primary.main' : 'info.main',
                      }}
                    />
                    <CompleteIcon
                      fontSize="small"
                      sx={{ color: picked ? 'primary.main' : 'transparent', flexShrink: 0 }}
                    />
                  </Box>
                );
              })}
            </Stack>

            {/* Custom fields for the selected provider service (auto-filled from this client's defaults) */}
            {(() => {
              const selSvc = b2bServices.find(s => String(s.id) === String(selectedB2bServiceId));
              const fields = selSvc?.customFields || selSvc?.custom_fields || [];
              if (!selSvc || fields.length === 0) return null;
              return (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>Service details</Typography>
                  {fields.map((f) => {
                    const fid = f.fieldId || f.tcCustomFieldId || f.id;
                    const val = b2bFieldValues[fid] ?? '';
                    const lbl = f.label + (f.required ? ' *' : '');
                    return (
                      <Box key={fid} sx={{ mb: 1.5 }}>
                        {f.fieldType === 'SELECT' ? (
                          <TextField select fullWidth size="small" label={lbl} value={val}
                            onChange={(e) => handleB2bFieldChange(fid, e.target.value)}>
                            <MenuItem value=""><em>None</em></MenuItem>
                            {(f.options || []).map((opt, i) => (
                              <MenuItem key={i} value={typeof opt === 'string' ? opt : (opt.value || '')}>
                                {typeof opt === 'string' ? opt : (opt.label || opt.value || '')}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField fullWidth size="small" label={lbl} value={val}
                            type={f.fieldType === 'NUMBER' ? 'number' : f.fieldType === 'DATE' ? 'date' : 'text'}
                            multiline={f.fieldType === 'TEXTAREA'} rows={f.fieldType === 'TEXTAREA' ? 2 : undefined}
                            InputLabelProps={f.fieldType === 'DATE' ? { shrink: true } : undefined}
                            onChange={(e) => handleB2bFieldChange(fid, e.target.value)} />
                        )}
                      </Box>
                    );
                  })}
                </>
              );
            })()}

            {/* Documents — required slots (auto-filled from the WO, or reuse/upload), plus the rest
                of the applicant's docs shared onto the B2B WO (same S3 object). */}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>Documents</Typography>

            {/* Required documents for the selected service */}
            {(() => {
              const selSvc = b2bServices.find(s => String(s.id) === String(selectedB2bServiceId));
              const reqs = selSvc?.documentRequirements || selSvc?.document_requirements || [];
              if (reqs.length === 0) return null;
              const assignedIds = new Set(Object.values(reqDocPick));
              return (
                <Box sx={{ mb: 1 }}>
                  {reqs.map((r) => {
                    const pickedId = reqDocPick[r.code];
                    const pickedDoc = pickedId ? b2bDocs.find(d => d.id === pickedId) : null;
                    const staged = b2bStagedDocs.filter(s => s.documentType === r.code);
                    const covered = !!pickedDoc || staged.length > 0;
                    // Existing docs available to assign to this slot (not used by another slot).
                    const available = b2bDocs.filter(d => !assignedIds.has(d.id) || d.id === pickedId);
                    return (
                      <Box key={r.code} sx={{ border: '1px solid', borderColor: covered ? 'success.light' : (r.required ? 'warning.light' : 'divider'), borderRadius: 1, p: 1, mb: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip size="small"
                            label={covered ? 'Attached' : (r.required ? 'Required' : 'Optional')}
                            color={covered ? 'success' : (r.required ? 'error' : 'default')}
                            sx={{ height: 20, fontSize: '0.65rem' }} />
                          <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: 500 }}>{r.label}{r.required ? ' *' : ''}</Typography>
                        </Box>
                        {pickedDoc ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <FileIcon fontSize="small" color="action" />
                            <Typography variant="caption" sx={{ flexGrow: 1 }} noWrap>
                              {pickedDoc.file_name || pickedDoc.fileName || (pickedDoc.document_type || pickedDoc.documentType)} <em>(existing)</em>
                            </Typography>
                            <IconButton size="small" onClick={() => clearReqPick(r.code)}><CloseIcon fontSize="small" /></IconButton>
                          </Box>
                        ) : staged.length > 0 ? (
                          staged.map((s) => (
                            <Box key={s.tempId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <FileIcon fontSize="small" color="action" />
                              <Typography variant="caption" sx={{ flexGrow: 1 }} noWrap>{s.fileName} <em>(new upload)</em></Typography>
                              <IconButton size="small" onClick={() => removeB2bStaged(s.tempId)}><CloseIcon fontSize="small" /></IconButton>
                            </Box>
                          ))
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                            {available.length > 0 && (
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <Select displayEmpty value="" onChange={(e) => pickExistingForReq(r.code, e.target.value)}>
                                  <MenuItem value="" disabled>Use an existing document…</MenuItem>
                                  {available.map(d => (
                                    <MenuItem key={d.id} value={d.id}>
                                      {d.file_name || d.fileName || (d.document_type || d.documentType)}
                                      {(d.document_type || d.documentType) ? ` · ${d.document_type || d.documentType}` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                            <Button size="small" component="label" startIcon={<UploadIcon fontSize="small" />} sx={{ textTransform: 'none' }}>
                              Upload new
                              <input hidden type="file" onChange={(e) => { stageB2bUpload(r.code, e.target.files); e.target.value = ''; }} />
                            </Button>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            {/* Other documents to share with the provider — all preselected, always visible. Docs
                already reused for a required slot are excluded here. */}
            {(() => {
              if (b2bDocs.length === 0) {
                return <Typography variant="caption" color="text.secondary">No documents for this customer.</Typography>;
              }
              const assignedIds = new Set(Object.values(reqDocPick));
              const extras = b2bDocs.filter(d => !assignedIds.has(d.id));
              if (extras.length === 0) return null;
              return (
                <Box>
                  <Typography variant="caption" color="text.secondary">Other documents to share with the provider</Typography>
                  {extras.map((d) => (
                    <FormControlLabel
                      key={d.id}
                      sx={{ display: 'flex', ml: 0 }}
                      control={
                        <Checkbox size="small" checked={selectedDocIds.includes(d.id)} onChange={() => toggleDocId(d.id)} />
                      }
                      label={
                        <Typography variant="body2">
                          {d.document_type || d.documentType || d.file_name || d.fileName}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              );
            })()}
            </>
              )}
            </>
          )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={handleCloseB2bDialog} disabled={b2bSubmitting} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleGiveToB2b}
            disabled={b2bSubmitting || !selectedB2bConnectionId || !selectedB2bServiceId}
            startIcon={b2bSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', minWidth: 128, borderRadius: 2 }}
          >
            {b2bSubmitting ? 'Delegating…' : 'Delegate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Employee Dialog (for Individual Internal Tasks) */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="span">Assign Task</Typography>
            {selectedTask && (
              <Typography variant="body2" color="text.secondary">
                Task: {selectedTask.taskName || selectedTask.task_name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={() => setAssignDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search employees..."
              value={employeeSearchQuery}
              onChange={(e) => setEmployeeSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ mb: 2 }}
            />

            {loadingEmployees ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={30} />
              </Box>
            ) : filteredEmployees.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography color="text.secondary">
                  No employees found
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredEmployees.map((employee) => (
                  <ListItem
                    key={employee.id}
                    button
                    onClick={() => handleAssignTask(employee)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AssignIcon sx={{ color: 'secondary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={employee.fullName || employee.full_name || employee.name}
                      secondary={employee.role || employee.position || 'Employee'}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: '0.8rem', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Assign Work Order Dialog (assigns entire WO to employee) */}
      <Dialog open={woAssignDialogOpen} onClose={() => setWoAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="span">Assign Work Order</Typography>
            <Typography variant="body2" color="text.secondary">
              {workOrder?.work_order_number || workOrder?.workOrderNumber}
            </Typography>
          </Box>
          <IconButton onClick={() => setWoAssignDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Assigning this work order will also auto-assign all internal tasks to the selected employee.
            Thaklis tasks (linked to tickets) will not be affected.
          </Alert>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search employees..."
              value={employeeSearchQuery}
              onChange={(e) => setEmployeeSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
              sx={{ mb: 2 }}
            />

            {loadingEmployees ? (
              <Box display="flex" justifyContent="center" py={3}>
                <CircularProgress size={30} />
              </Box>
            ) : filteredEmployees.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography color="text.secondary">
                  No employees found
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {filteredEmployees.map((employee) => (
                  <ListItem
                    key={employee.id}
                    button
                    onClick={() => handleAssignWorkOrder(employee)}
                    disabled={assigningWorkOrder}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <AssignIcon sx={{ color: 'secondary.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={employee.fullName || employee.full_name || employee.name}
                      secondary={employee.role || employee.position || 'Employee'}
                      primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: '0.8rem', color: 'text.secondary' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <AddServiceDialog
        open={addServiceDialogOpen}
        onClose={() => setAddServiceDialogOpen(false)}
        onSubmit={handleAddLineItem}
        submitting={addingService}
        workOrder={workOrder}
        customers={woCustomers}
        preferredCustomerName={detailView === 'customer' && activeGroup && activeGroup.key !== '__general' ? activeGroup.name : null}
        showThaklisServices={showThaklisServices}
      />

      <AddServiceDialog
        open={Boolean(editingLineItem)}
        onClose={() => setEditingLineItem(null)}
        onSubmit={handleUpdateLineItem}
        submitting={updatingService}
        workOrder={workOrder}
        customers={woCustomers}
        showThaklisServices={false}
        initialItem={editingLineItem}
      />

      {/* Document Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => { setPreviewDialogOpen(false); setPreviewUrl(''); setPreviewDocument(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 600, pr: 2, wordBreak: 'break-all' }}>
            {previewDocument?.file_name || previewDocument?.fileName || 'Document'}
          </Typography>
          <IconButton size="small" onClick={() => { setPreviewDialogOpen(false); setPreviewUrl(''); setPreviewDocument(null); }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ minHeight: 320 }}>
          {loadingPreview ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : !previewUrl ? (
            <Typography color="text.secondary" textAlign="center" py={6}>Could not load the document.</Typography>
          ) : getFilePreviewType(previewDocument?.file_name || previewDocument?.fileName) === 'image' ? (
            <Box textAlign="center">
              <img
                src={previewUrl}
                alt={previewDocument?.file_name || previewDocument?.fileName || 'document'}
                style={{ maxWidth: '100%', maxHeight: '72vh', objectFit: 'contain' }}
              />
            </Box>
          ) : getFilePreviewType(previewDocument?.file_name || previewDocument?.fileName) === 'pdf' ? (
            <Box component="iframe" title="document-preview" src={previewUrl}
              sx={{ width: '100%', height: '72vh', border: 'none' }} />
          ) : (
            <Box textAlign="center" py={5}>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                This file type can't be previewed here.
              </Typography>
              <Button variant="outlined" onClick={() => window.open(previewUrl, '_blank', 'noopener')} sx={{ textTransform: 'none' }}>
                Open in new tab
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => window.open(previewUrl, '_blank', 'noopener')} disabled={!previewUrl} sx={{ textTransform: 'none' }}>
            Open in new tab
          </Button>
          <Button onClick={() => { setPreviewDialogOpen(false); setPreviewUrl(''); setPreviewDocument(null); }} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Invoice - Mapped Organization Picker */}
      <Dialog open={createInvoiceDialogOpen} onClose={() => setCreateInvoiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Mapped Organization</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select the mapped organization to bill this invoice to.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Mapped Organization</InputLabel>
            <Select
              value={selectedMappedOrg?.id || ''}
              label="Mapped Organization"
              onChange={(e) => {
                const org = mappedOrgOptions.find(o => o.id === e.target.value);
                setSelectedMappedOrg(org || null);
              }}
            >
              {mappedOrgOptions.map(org => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}{org.code ? ` (${org.code})` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateInvoiceDialogOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => submitCreateInvoice(selectedMappedOrg)}
            disabled={!selectedMappedOrg || actionLoading}
          >
            {actionLoading ? 'Creating...' : 'Create Invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Line Item Markup Dialog — mirrors the invoice/quotation flow.
          Input is markup INCLUDING tax; split client-side with LINE_ITEM_VAT_RATE
          and sent to the backend as markupAmount + taxOnMarkup. */}
      <Dialog open={markupDialogOpen} onClose={closeLineItemMarkupDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon color="success" />
            {selectedLineItem && parseFloat(selectedLineItem.markup_amount || selectedLineItem.markupAmount) > 0
              ? 'Edit Markup' : 'Add Markup'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLineItem && (
            <>
              <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedLineItem.service_name || selectedLineItem.serviceName}
                </Typography>
                {(selectedLineItem.service_description || selectedLineItem.serviceDescription) && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedLineItem.service_description || selectedLineItem.serviceDescription}
                  </Typography>
                )}
              </Box>

              <Box sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.1)' : '#f0fdf4', p: 2, borderRadius: 1, mb: 2 }}>
                {(() => {
                  const markupInclTax = parseFloat(lineItemMarkupAmount) || 0;
                  const markupExclTax = markupInclTax / (1 + LINE_ITEM_VAT_RATE);
                  const taxOnMarkup = markupInclTax - markupExclTax;
                  const baseService = parseFloat(selectedLineItem.service_charge || selectedLineItem.serviceCharge) || 0;
                  const baseTax = parseFloat(selectedLineItem.tax_value || selectedLineItem.taxValue) || 0;
                  const newService = baseService + markupExclTax;
                  const newTax = baseTax + taxOnMarkup;
                  return (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        SERVICE CHARGE
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">Base:</Typography>
                        <Typography variant="body2">{formatCurrency(baseService)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">+ Markup:</Typography>
                        <Typography variant="body2" color="success.main">+{formatCurrency(markupExclTax)}</Typography>
                      </Box>

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        TAX (5%)
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">Base:</Typography>
                        <Typography variant="body2">{formatCurrency(baseTax)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" color="text.secondary">+ Tax on Markup:</Typography>
                        <Typography variant="body2" color="success.main">+{formatCurrency(taxOnMarkup)}</Typography>
                      </Box>

                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="body2" fontWeight={600}>Total Markup (incl. tax):</Typography>
                        <Typography variant="body2" fontWeight={600} color="success.main">
                          +{formatCurrency(markupInclTax)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">New Service Charge:</Typography>
                        <Typography variant="body2">{formatCurrency(newService)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">New Tax:</Typography>
                        <Typography variant="body2">{formatCurrency(newTax)}</Typography>
                      </Box>
                    </>
                  );
                })()}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Markup (Incl. Tax)"
                    value={lineItemMarkupAmount}
                    onChange={(e) => setLineItemMarkupAmount(e.target.value)}
                    fullWidth
                    type="number"
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography> }}
                    helperText="Amount to add as markup on this line"
                    autoFocus
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Markup (Excl. Tax)"
                    value={lineItemMarkupAmount ? (parseFloat(lineItemMarkupAmount) / (1 + LINE_ITEM_VAT_RATE)).toFixed(2) : ''}
                    fullWidth
                    disabled
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography> }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Tax"
                    value={lineItemMarkupAmount
                      ? (parseFloat(lineItemMarkupAmount) - parseFloat(lineItemMarkupAmount) / (1 + LINE_ITEM_VAT_RATE)).toFixed(2)
                      : ''}
                    fullWidth
                    disabled
                    InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>AED</Typography> }}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeLineItemMarkupDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApplyLineItemMarkup}
            disabled={applyingLineItemMarkup || !lineItemMarkupAmount || parseFloat(lineItemMarkupAmount) <= 0}
            startIcon={applyingLineItemMarkup ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {applyingLineItemMarkup ? 'Applying...' : 'Apply Markup'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkOrderDetail;
